<template>
  <div class="link-card bookmark-item queue-item"
    :id="`bookmark-${item.id}`"
    :class="[{ 'is-list': listMode, 'is-image-only': showImage && !hasAnyContent, 'hide-link-type': !showLinkType, 'selection-mode': selectionMode, 'is-selected': selected, 'quick-actions-enabled': quickActions }, `type-${item.type?.toLowerCase() || 'generic'}`]"
    ref="cardElement"
    @touchstart="onCardTouchStart"
    @touchmove="onCardTouchMove"
    @touchend="onCardTouchEnd"
    @touchcancel="onCardTouchCancel"
    @contextmenu="onCardContextMenu"
    @click="selectionMode ? $emit('toggle-select', item.id) : undefined"
  >
    <!-- Selection Checkbox Overlay -->
    <div v-if="selectionMode" class="selection-checkbox-overlay" @click.stop="$emit('toggle-select', item.id)">
      <div class="selection-checkbox" :class="{ checked: selected }">
        <i v-if="selected" class="pi pi-check"></i>
      </div>
    </div>

    <!-- Background Actions Layer (Mobile Swipe). Labels arrive translated from the
         parent (`swipeLabels`) — this file must stay i18n-free for the embed bundle. -->
    <div class="card-swipe-actions" :class="{ armed: swipeArmed }" v-if="isMobile && quickActions" aria-hidden="true">
       <div class="swipe-action left" :class="swipeRightAction ? `tone-${swipeRightAction.tone}` : ''" :style="{ opacity: cardSwipeOffset > 0 ? 1 : 0 }">
           <div v-if="swipeRightAction" class="action-content">
             <i :class="swipeRightAction.icon" style="font-size: 1.5rem; margin-bottom: 0.25rem;"></i>
             <span style="font-weight: 600;">{{ swipeRightAction.label }}</span>
           </div>
       </div>
       <div class="swipe-action right" :class="[isAnimatingDelete ? 'tone-danger' : (swipeLeftAction ? `tone-${swipeLeftAction.tone}` : ''), { 'full-mode': isAnimatingDelete }]" :style="{ opacity: (cardSwipeOffset < 0 || isAnimatingDelete) ? 1 : 0 }">
           <!-- While the shatter plays this panel covers the whole card (full-mode),
                so it must read as the delete it belongs to, not as whatever the
                LEFT swipe happens to be mapped to. -->
           <div v-if="swipeLeftAction && !isAnimatingDelete" class="action-content">
             <i :class="swipeLeftAction.icon" style="font-size: 1.5rem; margin-bottom: 0.25rem;"></i>
             <span style="font-weight: 600;">{{ swipeLeftAction.label }}</span>
           </div>
       </div>
    </div>
    
    <!-- Inner Content Wrapper for Swipe Effect -->
    <div class="card-inner-content" :style="[cardSwipeStyle, isAnimatingDelete ? { background: 'transparent', boxShadow: 'none' } : {}]">
    
    <!-- Archive Animation Overlay -->
    <div v-if="isAnimatingArchive" class="archive-animation-overlay">
        <div class="archive-icon-wrapper">
             <i class="pi pi-inbox"></i>
        </div>
    </div>

    <!-- Delete Animation Overlay (Break pieces) -->
    <div v-if="isAnimatingDelete" class="delete-animation-container">
        <!-- 6 pieces to simulate breaking -->
        <div class="shatter-piece p1"></div>
        <div class="shatter-piece p2"></div>
        <div class="shatter-piece p3"></div>
        <div class="shatter-piece p4"></div>
        <div class="shatter-piece p5"></div>
        <div class="shatter-piece p6"></div>
    </div>

    <!-- Main Content (Hide when deleting to show pieces) -->
    <div class="card-main-content" :style="{ opacity: isAnimatingDelete ? 0 : 1 }">
    <div v-if="showImage" class="item-image" @click="handleImageClick"
      :class="{ 'clickable': !!item.url, 'has-gallery': hasImageGallery }" @mouseenter="onImageHover"
      @mouseleave="onImageLeave" @mousemove="onImageMouseMove" 
      @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd"
      ref="imageContainer">
      <div v-if="item.isAnalyzing" class="analyzing-visual">
        <div class="analyzing-grid"></div>
        <div class="analyzing-halo"></div>
        <div class="analyzing-scanner"></div>
        <div class="analyzing-icon">
          <i class="pi pi-sparkles"></i>
        </div>
      </div>
      <img v-else-if="currentDisplayImage" :src="currentDisplayImage" :alt="item.title" @error="onImgError"
        @load="onImageLoad"
        :loading="eager ? 'eager' : 'lazy'"
        :fetchpriority="eager ? 'high' : 'auto'"
        decoding="async"
        width="320" height="200"
        :class="{ 'gallery-contain': hasImageGallery && currentImageIndex > 0, 'reddit-fallback': isRedditFallback }" />
      <div v-else-if="shouldShowFileIcon" class="file-icon-placeholder">
        <i :class="fileIcon" style="font-size: 3rem;"></i>
        <span class="file-extension" v-if="item.file_extension">{{ item.file_extension.toUpperCase() }}</span>
      </div>
      <div v-else class="image-icon-placeholder">
        <ImageIcon :size="48" :stroke-width="1.5" />
      </div>
      <!-- Image gallery indicators -->
      <div v-if="hasImageGallery && (isHoveringImage || isMobile)" class="gallery-indicators" :class="{ 'mobile-visible': isMobile }">
        <div v-for="(img, index) in imageGallery" :key="index" class="gallery-indicator-dot"
          :class="{ 'active': index === currentImageIndex }"></div>
      </div>
      
      <!-- Reminder indicator (Top Left) -->
      <div v-if="item.reminder_at" class="reminder-indicator-image" v-tooltip.bottom="reminderTooltip" :title="reminderTooltip">
        <Bell :size="14" />
      </div>

      <!-- Price alert indicator (Top Left, next to reminder) -->
      <div v-if="hasPriceAlert" class="price-alert-indicator-image" :style="{ left: item.reminder_at ? '38px' : '8px' }" v-tooltip.bottom="'Price alert active'" :title="'Price alert active'">
        <TrendingDown :size="14" />
      </div>

      <!-- Waiting-to-sync badge (Top Left, after the others). Image-only layout only:
           every other layout shows the labelled row in .item-content instead. -->
      <div v-if="isAwaitingSync && !hasAnyContent" class="pending-sync-indicator-image" role="img"
        :aria-label="pendingSyncText" :style="{ left: pendingSyncBadgeLeft }" v-tooltip.bottom="pendingSyncText">
        <CloudUpload :size="14" aria-hidden="true" />
      </div>

      <!-- Highlight count indicator (Top Right) -->
      <div v-if="showHighlightCount && highlightCount > 0" class="highlight-count-indicator-image" v-tooltip.bottom="highlightCount + ' highlight' + (highlightCount === 1 ? '' : 's') + ' on this page'" :title="highlightCount + ' highlight' + (highlightCount === 1 ? '' : 's')">
        <Highlighter :size="12" />
        <span class="highlight-count-num">{{ highlightCount }}</span>
      </div>

      <!-- Video indicator overlay for VIDEO type with embed_url -->
      <!-- <div 
        v-if="item.type === 'VIDEO' && hasEmbedUrl" 
        class="youtube-indicator"
      >
        <i class="pi pi-play"></i>
      </div> -->
      <!-- Offline available indicator -->
      <div v-if="item.actionType === 'Read' && isOfflineAvailable" class="offline-available-indicator"
        v-tooltip.bottom="'Available offline'">
        <i class="pi pi-download"></i>
      </div>
      <!-- Reading progress indicator -->
      <div v-if="item.actionType === 'Read' && readingProgress && readingProgress.percentRead > 5"
        class="reading-progress-indicator" :class="{ 'completed': readingProgress.percentRead >= 99 }"
        v-tooltip.bottom="readingProgress.percentRead >= 99 ? 'Completed! ✓' : `${readingProgress.percentRead}% read`">
        <div class="progress-ring">
          <svg width="32" height="32">
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#667eea;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#764ba2;stop-opacity:1" />
              </linearGradient>
            </defs>
            <circle v-if="readingProgress.percentRead >= 99" class="progress-ring-circle-filled" cx="16" cy="16"
              r="14" />
            <circle v-else class="progress-ring-circle-bg" cx="16" cy="16" r="14" />
            <circle class="progress-ring-circle" cx="16" cy="16" r="14" :style="{
              strokeDashoffset: 88 - (88 * readingProgress.percentRead / 100)
            }" />
          </svg>
          <span class="progress-text" :class="{ 'completed': readingProgress.percentRead >= 99 }">
            {{ readingProgress.percentRead >= 99 ? '✓' : `${readingProgress.percentRead}%` }}
          </span>
        </div>
      </div>
      <!-- Pinned indicator -->
      <div v-if="item.pinned" class="pinned-indicator" v-tooltip.bottom="'Pinned to top'">
        <Pin :size="16" />
      </div>
      <!-- Link Type Icon Indicator -->
      <div v-if="showLinkTypeIcon" class="link-type-indicator" 
           :class="{ 'has-reading-progress': item.actionType === 'Read' && readingProgress && readingProgress.percentRead > 5 }"
           v-tooltip.bottom="item.type">
        <component :is="typeIconComponent" :size="16" />
      </div>
      <!-- Mobile three-dot menu button -->
      <Button v-if="isMobile && showActions" icon="pi pi-ellipsis-v" class="p-button-text p-button-sm mobile-menu-button"
        @click.stop="toggleMobileMenu" aria-label="Actions menu" />

      <!-- Action buttons overlay - bottom right -->
      <div v-if="showActions && !isMobile" class="item-actions-overlay">
        <!-- Show three-dots menu when card is narrow -->
        <Button v-if="isNarrowCard" icon="pi pi-ellipsis-h" class="p-button-text p-button-sm action-button compact-menu-button"
          @click.stop="toggleMobileMenu" v-tooltip.top="'More actions'" aria-label="Actions menu" />
        <!-- Show individual action buttons when card is wide enough -->
        <template v-else>
          <!-- <Button icon="pi pi-external-link" class="p-button-text p-button-sm action-button" @click.stop="openUrl"
            v-tooltip="'Open link'" /> -->
          <Button v-if="item.type === 'ARTICLE'" icon="pi pi-book" class="p-button-text p-button-sm action-button"
            @click.stop="$emit('read', item.id)" v-tooltip.top="'Read article'" />
          <Button v-if="item.type === 'ARTICLE'" icon="pi pi-sparkles" class="p-button-text p-button-sm action-button"
            @click.stop="$emit('see-summary', item)" v-tooltip.top="'See summary'" />
          <Button v-if="item.type === 'VIDEO' && hasEmbedUrl" icon="pi pi-play"
            class="p-button-text p-button-sm action-button" @click.stop="$emit('watch', item)"
            v-tooltip.top="'Watch video'" />
          <Button v-if="(item.vaultId || item.vault_id) && isPro" icon="pi pi-lock"
            class="p-button-text p-button-sm action-button vault-button" @click.stop="openVaultVersion"
            v-tooltip.top="'View Vault Version'" />
          <Button v-if="canEdit" icon="pi pi-pencil" class="p-button-text p-button-sm action-button" @click.stop="$emit('edit', item)"
            v-tooltip.top="'Edit'" />
          <Button v-if="canDelete" icon="pi pi-trash" class="p-button-text p-button-sm p-button-danger action-button"
            @click.stop="$emit('delete', item.id)" v-tooltip.top="'Delete'" />
        </template>
      </div>
      <!-- Rating buttons overlay - bottom left -->
      <div v-if="showActions" class="item-rating-overlay">
        <Button :class="['p-button-text', 'p-button-sm', 'rating-button', { 'rated': item.rating === 'UP' }]"
          @click.stop="handleRatingClick('UP')" v-tooltip.top="getRatingTooltip('UP')">
          <ThumbsUp :size="20" />
        </Button>
        <Button :class="['p-button-text', 'p-button-sm', 'rating-button', { 'rated': item.rating === 'DOWN' }]"
          @click.stop="handleRatingClick('DOWN')" v-tooltip.top="getRatingTooltip('DOWN')">
          <ThumbsDown :size="20" />
        </Button>
        <Button v-if="item.rating && item.rating !== 'NONE'" :class="['p-button-text', 'p-button-sm', 'rating-button']"
          @click.stop="handleRatingClick('NONE')" v-tooltip.top="'Clear rating'">
          <CircleSlash :size="20" />
        </Button>
      </div>
    </div>

    <!-- Mobile menu (placed outside image container) -->
    <Menu ref="mobileMenu" :model="mobileMenuItems" :popup="true" class="mobile-actions-menu" />

    <!-- Mobile three-dot menu button (when no image shown) -->
    <Button v-if="isMobile && !showImage && showActions" icon="pi pi-ellipsis-v"
      class="p-button-text p-button-sm mobile-menu-button no-image" @click.stop="toggleMobileMenu"
      aria-label="Actions menu" />

    <div class="item-content" v-if="hasAnyContent">
      <h3 v-if="showTitle" class="item-title clickable" v-tooltip.bottom="item.title" @click="openUrl">
        {{ item.title }}
        <span v-if="item.reminder_at && !showImage" class="reminder-icon-title" v-tooltip.top="reminderTooltip" :title="reminderTooltip">
          <Bell :size="14" />
        </span>
      </h3>
      <div class="item-meta" v-if="showDate">
        <span v-if="showDate" class="item-date">{{ formattedDate }}</span>
      </div>
      <p class="item-url" v-if="showUrl && item.url" v-tooltip.bottom="item.url">{{ truncatedUrl }}</p>
      
      <!-- Analyzing state -->
      <div v-if="item.isAnalyzing" class="analyzing-state">
        <i class="pi pi-spin pi-spinner" style="font-size: 1rem; margin-right: 0.5rem;"></i>
        <span>Analyzing...</span>
      </div>

      <!-- Save parked on this device (offline queue): explains the URL title and
           why rating/reader refuse until it syncs. -->
      <div v-if="isAwaitingSync" class="pending-sync-state">
        <CloudUpload :size="14" aria-hidden="true" />
        <span>{{ pendingSyncText }}</span>
      </div>

      <div class="item-tags" v-if="showTags && item.tags && item.tags.length">
        <Tag v-for="tag in item.tags" :key="tag.name || tag" class="tag-small" :style="{
          backgroundColor: (tag.color || '#888888') + '20',
          color: tag.color || '#888888',
          border: `1px solid ${tag.color || '#888888'}`,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem'
        }">
          <template #default>
            <component v-if="tag.icon" :is="getIconComponent(tag.icon)" :size="12" :color="tag.color || '#888888'"
              class="tag-icon" />
            <span>{{ tag.name || tag }}</span>
          </template>
        </Tag>
      </div>
      <!-- Knowledge card chips -->
      <div class="item-knowledge-cards" v-if="showKnowledgeCards && item.knowledgeCards && item.knowledgeCards.length">
        <router-link
          v-for="kc in item.knowledgeCards"
          :key="kc.id"
          :to="{ path: '/knowledge', query: { open: kc.id } }"
          class="knowledge-card-chip"
          v-tooltip="'Open in Knowledge'"
        >
          <Brain :size="12" />
          <span>{{ kc.title }}</span>
        </router-link>
      </div>
      <!-- Bookmark metadata for special types -->
      <BookmarkMetadata v-if="showAutomaticMetadata && item.type && item.type !== 'GENERIC' && !item.isAnalyzing" :type="item.type"
        :metadata="item.metaData || item.meta_data || {}" :title="item.title" :link-id="item.id"
        :show-streaming-availability="showStreamingAvailability && !publicView" />

      <!-- Image description for IMAGE file_type -->
      <div v-if="showAutomaticMetadata && imageDescription && !item.isAnalyzing" class="image-description-section">
        <!-- Debug: file_type={{ item.file_type }} -->
        <span class="image-description-text">{{ imageDescription }}</span>
      </div>

      <!-- Twitter/X Embed -->
      <div v-if="showAutomaticMetadata && isTwitterUrl" class="twitter-embed-section" ref="twitterEmbedContainer"></div>

      <!-- Custom user-defined metadata -->
      <div v-if="showCustomMetadata && customMetadata.length > 0" class="custom-metadata-section">
        <div v-for="meta in customMetadata" :key="meta.key" class="custom-metadata-item"
          :class="{ 'preset-metadata': meta.isPreset }">
          <!-- Price with currency (preset format) -->
          <template v-if="meta.key === 'price' && meta.currency">
            <DollarSign :size="14" />
            <span class="metadata-value">{{ formatPrice(meta.value, meta.currency) }}</span>
          </template>

          <!-- Rating (preset format) -->
          <template v-else-if="meta.key === 'rating'">
            <Star :size="14" :fill="'#ff69fc'" :color="'#ff69fc'" />
            <span class="metadata-value">{{ formatRating(meta.value) }}</span>
          </template>

          <!-- Address (preset format) -->
          <template v-else-if="meta.key === 'address'">
            <MapPin :size="14" />
            <span class="metadata-value">{{ formatAddress(meta.value) }}</span>
          </template>

          <!-- Standard metadata -->
          <template v-else>
            <span class="metadata-label">{{ formatAttributeName(meta.key) }}:</span>
            <a
              v-if="isLinkableMetadataValue(meta.value)"
              class="metadata-value metadata-link"
              :href="metadataUrl(meta.value)"
              target="_blank"
              rel="noopener noreferrer nofollow"
              @click.stop
            >{{ metadataUrl(meta.value) }}</a>
            <span v-else class="metadata-value">{{ formatMetadataValue(meta.value) }}</span>
          </template>
        </div>
      </div>

      <!-- Notes Section -->
      <div v-if="showNotes && item.notes" class="notes-section">
        <div class="notes-content" v-html="formattedNotes"></div>
      </div>

      <!-- AI article summary (Pro-only; shown when the "Article Summary" view setting is on) -->
      <div v-if="showArticleSummary && articleSummaryBullets.length" class="article-summary-section">
        <div class="article-summary-heading">
          <Sparkles :size="13" />
          <span>Summary</span>
        </div>
        <ul class="article-summary-list">
          <li v-for="(bullet, i) in articleSummaryBullets" :key="i">{{ bullet }}</li>
        </ul>
      </div>

      <!-- Action and Rating buttons when no image is shown (as overlay on hover) -->
      <div v-if="!showImage && showActions && !isMobile" class="content-actions-overlay">
        <!-- Rating buttons - bottom left -->
        <div class="content-rating-buttons">
          <Button :class="['p-button-text', 'p-button-sm', 'rating-button', { 'rated': item.rating === 'UP' }]"
            @click.stop="handleRatingClick('UP')" v-tooltip.top="getRatingTooltip('UP')">
            <ThumbsUp :size="20" />
          </Button>
          <Button :class="['p-button-text', 'p-button-sm', 'rating-button', { 'rated': item.rating === 'DOWN' }]"
            @click.stop="handleRatingClick('DOWN')" v-tooltip.top="getRatingTooltip('DOWN')">
            <ThumbsDown :size="20" />
          </Button>
          <Button v-if="item.rating && item.rating !== 'NONE'" :class="['p-button-text', 'p-button-sm', 'rating-button']"
            @click.stop="handleRatingClick('NONE')" v-tooltip.top="'Clear rating'">
            <CircleSlash :size="20" />
          </Button>
        </div>

        <!-- Action buttons - bottom right -->
        <div class="content-action-buttons">
          <!-- Show three-dots menu when card is narrow -->
          <Button v-if="isNarrowCard" icon="pi pi-ellipsis-h" class="p-button-text p-button-sm action-button compact-menu-button"
            @click.stop="toggleMobileMenu" v-tooltip.top="'More actions'" aria-label="Actions menu" />
          <!-- Show individual action buttons when card is wide enough -->
          <template v-else>
            <Button v-if="item.type === 'ARTICLE'" icon="pi pi-book" class="p-button-text p-button-sm action-button"
              @click.stop="$emit('read', item.id)" v-tooltip.top="'Read article'" />
            <Button v-if="item.type === 'ARTICLE'" icon="pi pi-sparkles" class="p-button-text p-button-sm action-button"
              @click.stop="$emit('see-summary', item)" v-tooltip.top="'See summary'" />
            <Button v-if="item.type === 'VIDEO' && hasEmbedUrl" icon="pi pi-play"
              class="p-button-text p-button-sm action-button" @click.stop="$emit('watch', item)"
              v-tooltip.top="'Watch video'" />
            <Button v-if="item.vaultId || item.vault_id" icon="pi pi-lock"
              class="p-button-text p-button-sm action-button vault-button" @click.stop="openVaultVersion"
              v-tooltip.top="'View Vault Version'" />
            <Button v-if="canEdit" icon="pi pi-pencil" class="p-button-text p-button-sm action-button" @click.stop="$emit('edit', item)"
              v-tooltip.top="'Edit'" />
            <Button v-if="canDelete" icon="pi pi-trash" class="p-button-text p-button-sm p-button-danger action-button"
              @click.stop="$emit('delete', item.id)" v-tooltip.top="'Delete'" />
          </template>
        </div>
      </div>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch, onBeforeUnmount, nextTick, inject } from 'vue'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Menu from 'primevue/menu'
import Skeleton from 'primevue/skeleton'
import * as LucideIcons from 'lucide-vue-next'
import {
  DollarSign, Star, MapPin, ThumbsUp, ThumbsDown, CircleSlash, Pin, Image as ImageIcon, Bell, TrendingDown, Brain, Highlighter, Sparkles, CloudUpload
} from 'lucide-vue-next'
import OfflineStorage from '../services/offlineStorage.js'
import ReadingProgress from '../services/readingProgress.js'
import BookmarkMetadata from './BookmarkMetadata.vue'
import { useMarkdownConverter } from '../composables/useMarkdownConverter.js'
import { useHighlightsStore } from '../composables/useHighlightsStore.js'
import { formatDate, formatDateTime, isHttpUrl, formatJsonMetadata, formatAttributeName } from '../utils/metadataFormatters.js'
import { sanitizeHtml, safeOpenUrl } from '../utils/security.js'
import { vaultMetaUrl } from '../utils/vaultUrl.js'
import { buildNonHostedImageRegex } from '../utils/constants.js'
import { shareLink as shareLinkViaWebShare } from '../utils/shareLink.js'
import { useIsMobile } from '../composables/useIsMobile.js'
import { QUICK_ACTION_META, resolveSwipeAction, createLongPressTracker } from '../utils/linkQuickActions.js'
import { isPendingBookmarkId } from '../utils/pendingBookmark.js'

// Built once at module scope (host is a compile-time constant) and reused across
// every card/render — avoids recompiling an identical regex per notes render.
// Safe to share: it's only used with String.replace(), which resets lastIndex.
const NON_HOSTED_IMAGE_REGEX = buildNonHostedImageRegex()

export default {
  name: 'LinkCard',
  components: {
    Button, Tag, Menu, Skeleton, BookmarkMetadata,
    DollarSign, Star, MapPin, ThumbsUp, ThumbsDown, CircleSlash, Pin, ImageIcon, Bell, TrendingDown, Brain, Highlighter, Sparkles, CloudUpload
  },
  props: {
    item: { type: Object, required: true },
    showTags: { type: Boolean, default: true },
    listMode: { type: Boolean, default: false },
    showUrl: { type: Boolean, default: true },
    showTitle: { type: Boolean, default: true },
    showImage: { type: Boolean, default: true },
    showDate: { type: Boolean, default: true },
    showLinkType: { type: Boolean, default: true },
    showLinkTypeIcon: { type: Boolean, default: false },
    showCustomMetadata: { type: Boolean, default: true },
    showAutomaticMetadata: { type: Boolean, default: true },
    showActions: { type: Boolean, default: true },
    showNotes: { type: Boolean, default: false },
    showArticleSummary: { type: Boolean, default: false },
    // Streaming availability chip on MOVIE/TV_SHOW cards. Default true (view-settings
    // toggle); combined with !publicView before reaching BookmarkMetadata, so public,
    // shared, and embed renders never mount (or lazily load) the streaming UI.
    showStreamingAvailability: { type: Boolean, default: true },
    isInbox: { type: Boolean, default: false },
    selectionMode: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    hasPriceAlert: { type: Boolean, default: false },
    showKnowledgeCards: { type: Boolean, default: false },
    showHighlightCount: { type: Boolean, default: true },
    permission: { type: String, default: 'OWNER' },
    /**
     * When true, the primary image is fetched with loading="eager" and
     * fetchpriority="high". Set this on the first few cards that are
     * above the fold so the browser can prioritise the LCP image.
     */
    eager: { type: Boolean, default: false },
    /**
     * True when rendered in a public / shared / embed (read-only) context. Custom metadata is a
     * Pro feature for the OWNER's own views, but on a public share the metadata belongs to the
     * (Pro) owner, so it must show regardless of the VIEWER's plan.
     */
    publicView: { type: Boolean, default: false },
    /**
     * Enables the mobile swipe layer plus long-press / right-click quick actions.
     * Off by default so read-only renders (public, embed, search results, meal
     * planner) never expose gestures nobody handles.
     */
    quickActions: { type: Boolean, default: false },
    // View-settings swipe mapping (ids from SWIPE_ACTION_IDS), resolved per link.
    swipeRight: { type: String, default: 'archive' },
    swipeLeft: { type: String, default: 'delete' },
    // Translated labels keyed by RESOLVED action id — the parent owns i18n.
    swipeLabels: { type: Object, default: () => ({}) },
    // Translated "waiting to sync" label for a card whose save is parked in the
    // offline queue. Same deal as swipeLabels: the parent owns i18n.
    pendingSyncLabel: { type: String, default: '' }
  },
  emits: ['edit', 'delete', 'read', 'watch', 'rate', 'toggle-select', 'open', 'see-summary', 'quick-action', 'quick-actions'],
  setup(props, { emit }) {
    const isPro = inject('isPro', ref(false))
    const { markdownToHtml } = useMarkdownConverter()
    const highlightsStore = useHighlightsStore()
    const highlightCount = computed(() => highlightsStore.getCount(props.item?.id))

    // Permission-based action visibility
    const canEdit = computed(() => ['OWNER', 'FULL'].includes(props.permission))
    const canDelete = computed(() => ['OWNER', 'FULL'].includes(props.permission))
    const isOfflineAvailable = ref(false)
    const readingProgress = ref(null)
    const { isMobile } = useIsMobile()
    const isNarrowCard = ref(false)
    const mobileMenu = ref(null)
    const cardElement = ref(null)

    // Image gallery state
    const currentImageIndex = ref(0)
    const isHoveringImage = ref(false)
    const imageContainer = ref(null)
    const firstImageHeight = ref(null)
    const imageLoadFailed = ref(false)
    const usingFallbackImage = ref(false)

    // Touch swipe state for mobile
    const touchStartX = ref(0)
    const touchStartY = ref(0)
    const touchEndX = ref(0)
    const isSwiping = ref(false)
    const swipeThreshold = 50 // minimum distance for swipe

    // Card swipe state for mobile quick actions. Which action each direction
    // fires comes from view settings (swipeRight/swipeLeft) and is resolved per
    // link in linkQuickActions.js so the gesture is never dead.
    const cardStartX = ref(0)
    const cardStartY = ref(0)
    const cardSwipeOffset = ref(0)
    const isCardSwiping = ref(false)
    const cardTransitionSmoothing = ref(true)
    // Card parked open while the delete confirm dialog is up (parent calls resetSwipe on cancel).
    const isHoldingForConfirm = ref(false)
    // Pull distances as a fraction of card width. The reveal starts at 1px, so
    // the trigger must sit well inside the cap or users release "on the label"
    // and nothing fires (was 40% of a 48% cap: a 30px window on a phone).
    const SWIPE_MAX_FRACTION = 0.48
    const SWIPE_TRIGGER_FRACTION = 0.3
    const swipeArmed = ref(false)

    // Animation States
    const isAnimatingArchive = ref(false)
    const isAnimatingDelete = ref(false)

    // Short on purpose: these run once per swipe down a list. Reduced-motion users get none.
    const ARCHIVE_FLASH_MS = 300
    const DELETE_SHATTER_MS = 450
    const prefersReducedMotion = () =>
      typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Only rendered when the parent passed no translated label (it always does in-app).
    const SWIPE_FALLBACK_LABELS = {
      archive: 'Archive', inbox: 'Inbox', delete: 'Delete', pin: 'Pin', unpin: 'Unpin', remind: 'Remind', menu: 'More'
    }

    // A swipe must respect the same permission gate as the ⋮ menu and the sheet
    // (both hide every write action for a READ_ONLY viewer). Without this, a
    // shared read-only collection still let a swipe fire archive/inbox/pin/
    // remind/delete, which the backend then rejects with an error toast for an
    // action the UI should never have offered. `menu` stays allowed: it only
    // opens the sheet, which gates its own rows.
    const canSwipeTo = (resolved) => {
      if (resolved === 'menu') return true
      if (resolved === 'delete') return canDelete.value
      return canEdit.value
    }

    const resolveSwipe = (actionId) => {
      const resolved = resolveSwipeAction(actionId, props.item)
      if (!resolved || !canSwipeTo(resolved)) return null
      const meta = QUICK_ACTION_META[resolved]
      return { id: resolved, icon: meta.icon, tone: meta.tone, label: props.swipeLabels[resolved] || SWIPE_FALLBACK_LABELS[resolved] }
    }
    const swipeRightAction = computed(() => resolveSwipe(props.swipeRight))
    const swipeLeftAction = computed(() => resolveSwipe(props.swipeLeft))

    const animateDelete = () => {
        return new Promise(resolve => {
            // Snap back to centre so the card covers the (now expanding) red background
            cardSwipeOffset.value = 0
            isHoldingForConfirm.value = false
            if (prefersReducedMotion()) { resolve(); return }
            isAnimatingDelete.value = true
            setTimeout(resolve, DELETE_SHATTER_MS)
        })
    }

    // Long press → quick-actions sheet (the parent renders it; this file only emits).
    let lastLongPressAt = 0
    const longPress = createLongPressTracker({
      onFire: () => {
        lastLongPressAt = Date.now()
        cardSwipeOffset.value = 0
        try { if (navigator.vibrate) navigator.vibrate(10) } catch (e) { /* unsupported */ }
        emit('quick-actions', { item: props.item, source: 'longpress' })
      }
    })

    const gesturesEnabled = () => isMobile.value && props.quickActions && props.showActions

    const onCardTouchStart = (e) => {
        if (!gesturesEnabled()) return
        if (isAnimatingArchive.value || isAnimatingDelete.value) return // Block if animating

        // A confirm dialog dismissed without invoking `reject` (Escape / Android
        // back — PrimeVue's onHide does not call it) leaves the card parked and
        // the flag latched; a new touch is an unambiguous point to clear both.
        if (isHoldingForConfirm.value) {
            isHoldingForConfirm.value = false
            cardSwipeOffset.value = 0
        }

        const touch = e.touches[0]
        cardStartX.value = touch.clientX
        cardStartY.value = touch.clientY
        cardTransitionSmoothing.value = false
        isCardSwiping.value = false
        if (!props.selectionMode) longPress.start(touch.clientX, touch.clientY)
    }

    const onCardTouchMove = (e) => {
        if (!gesturesEnabled()) return

        const touch = e.touches[0]
        const diffX = touch.clientX - cardStartX.value
        const diffY = touch.clientY - cardStartY.value
        longPress.move(touch.clientX, touch.clientY)

        // If the image gallery swipe is already active, don't swipe the card
        if (isSwiping.value) { longPress.cancel(); return }

        // Check if vertical scrolling
        if (!isCardSwiping.value && Math.abs(diffY) > Math.abs(diffX)) {
            return
        }

        // Lock into horizontal swipe
        if (Math.abs(diffX) > 10) {
             // Over a gallery image the horizontal swipe belongs to the gallery
             if (hasImageGallery.value && isHoveringImage.value) {
                 return
             }
            longPress.cancel()
            isCardSwiping.value = true
            if (e.cancelable) e.preventDefault()
        }

        if (isCardSwiping.value && !props.selectionMode) {
            let newOffset = diffX

            // A direction with no action resolves to nothing — don't let it pull
            if (newOffset > 0 && !swipeRightAction.value) newOffset = 0
            if (newOffset < 0 && !swipeLeftAction.value) newOffset = 0

            // Limit max swipe distance to prevent pulling too far
            const width = cardElement.value ? cardElement.value.offsetWidth : window.innerWidth
            const maxSwipe = width * SWIPE_MAX_FRACTION

            if (newOffset > maxSwipe) newOffset = maxSwipe
            if (newOffset < -maxSwipe) newOffset = -maxSwipe

            cardSwipeOffset.value = newOffset

            const armed = Math.abs(newOffset) >= width * SWIPE_TRIGGER_FRACTION
            if (armed !== swipeArmed.value) {
                swipeArmed.value = armed
                if (armed) { try { if (navigator.vibrate) navigator.vibrate(6) } catch (e) { /* unsupported */ } }
            }
        }
    }

    const fireSwipeAction = (action, width, direction) => {
        if (action.id === 'delete') {
            // The parent shows the confirm dialog; the card stays parked open until it
            // either calls resetSwipe (cancel) or animateDelete (confirm).
            isHoldingForConfirm.value = true
            const resetSwipe = () => {
                isHoldingForConfirm.value = false
                cardSwipeOffset.value = 0
            }
            emit('quick-action', { id: 'delete', item: props.item, resetSwipe, animateDelete })
            cardSwipeOffset.value = direction * width * SWIPE_MAX_FRACTION
            return
        }
        if (action.id === 'archive') {
            startArchiveAnimation(() => emit('quick-action', { id: 'archive', item: props.item }))
            return
        }
        cardSwipeOffset.value = 0
        emit('quick-action', { id: action.id, item: props.item })
    }

    const onCardTouchEnd = (e) => {
        if (!gesturesEnabled()) return
        if (longPress.end()) {
            // The press opened the sheet — swallow the click that would follow.
            if (e && e.cancelable) e.preventDefault()
            return
        }
        if (!isCardSwiping.value) return

        cardTransitionSmoothing.value = true

        const width = cardElement.value ? cardElement.value.offsetWidth : window.innerWidth
        const threshold = width * SWIPE_TRIGGER_FRACTION
        swipeArmed.value = false

        if (cardSwipeOffset.value > threshold && swipeRightAction.value) {
            fireSwipeAction(swipeRightAction.value, width, 1)
        } else if (cardSwipeOffset.value < -threshold && swipeLeftAction.value) {
            fireSwipeAction(swipeLeftAction.value, width, -1)
        } else {
            cardSwipeOffset.value = 0
        }

        setTimeout(() => {
            isCardSwiping.value = false
            // Reset unless the card is parked for a delete confirm or mid-archive flash
            if (!isHoldingForConfirm.value && !isAnimatingArchive.value) {
                cardSwipeOffset.value = 0
            }
        }, 300)
    }

    const onCardTouchCancel = () => {
        longPress.cancel()
        swipeArmed.value = false
        if (isCardSwiping.value && !isHoldingForConfirm.value) {
            cardTransitionSmoothing.value = true
            cardSwipeOffset.value = 0
            isCardSwiping.value = false
        }
    }

    // Right-click (desktop) opens the same sheet. Android also fires contextmenu
    // after a long press, so a press that just fired must not open it twice.
    const onCardContextMenu = (e) => {
        if (!props.quickActions || !props.showActions || props.selectionMode) return
        e.preventDefault()
        if (Date.now() - lastLongPressAt < 800) return
        emit('quick-actions', { item: props.item, source: 'contextmenu' })
    }

    const startArchiveAnimation = (done) => {
        cardSwipeOffset.value = 0
        if (prefersReducedMotion()) { done(); return }
        isAnimatingArchive.value = true
        setTimeout(() => {
            done()
            // The card usually unmounts here (it leaves the inbox); if not, reset.
            isAnimatingArchive.value = false
        }, ARCHIVE_FLASH_MS)
    }

    const cardSwipeStyle = computed(() => {
        return {
            transform: `translateX(${cardSwipeOffset.value}px)`,
            transition: cardTransitionSmoothing.value ? 'transform 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)' : 'none'
        }
    })

    // Check if article is available offline and has reading progress
    const checkOfflineAvailability = async () => {
      if (props.item.actionType === 'Read' && props.item.id) {
        isOfflineAvailable.value = await OfflineStorage.hasArticle(props.item.id)
      }
    }

    // Static import on purpose: readingProgress.js imports nothing, so a lazy
    // chunk only gave a stale tab one more asset to lose after a deploy
    // (LINKFLARE-1Z / LINKFLARE-E).
    const checkReadingProgress = () => {
      if (props.item.actionType === 'Read' && props.item.id) {
        readingProgress.value = ReadingProgress.getPosition(props.item.id)
      }
    }

    onMounted(() => {
      checkOfflineAvailability()
      checkReadingProgress()

      // Set up ResizeObserver to detect narrow cards
      nextTick(() => {
        if (cardElement.value) {
          const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
              const width = entry.contentRect.width
              // Consider card narrow if width is less than 250px
              isNarrowCard.value = width < 250
            }
          })

          resizeObserver.observe(cardElement.value)
          
          // Store observer for cleanup
          cardElement.value._resizeObserver = resizeObserver
        }
      })
    })

    // Watch for changes in the item (e.g., when _progressUpdated changes)
    watch(() => props.item._progressUpdated, () => {
      checkReadingProgress()
    })

    // Reset image error state when image URL changes (e.g., after re-analysis)
    watch(() => props.item.image, () => {
      imageLoadFailed.value = false
      usingFallbackImage.value = false
    })

    // Watch for view mode changes and reset height if not in masonry mode.
    // The observer is stored and disconnected on container change / unmount (H7) —
    // otherwise observers accumulate across view changes and re-run on every grid
    // class mutation.
    let classMutationObserver = null
    watch(() => imageContainer.value, (newContainer, _oldContainer, onCleanup) => {
      if (newContainer) {
        // Set up a MutationObserver to watch for class changes on parent
        const observer = new MutationObserver(() => {
          const isMasonryMode = newContainer.closest('.masonry-mode')
          if (!isMasonryMode && newContainer.style.height) {
            // Clear the inline height style when not in masonry mode
            newContainer.style.height = ''
            firstImageHeight.value = null
          }
        })
        classMutationObserver = observer

        // Observe the parent grid container for class changes
        const gridContainer = newContainer.closest('.links-grid')
        if (gridContainer) {
          observer.observe(gridContainer, { attributes: true, attributeFilter: ['class'] })
        }

        // Disconnect when the container changes or the watcher is stopped (unmount).
        onCleanup(() => {
          observer.disconnect()
          if (classMutationObserver === observer) classMutationObserver = null
        })
      }
    }, { immediate: true })

    // Extract original image URL from metadata (before R2 rehosting)
    const originalImageUrl = computed(() => {
      const metadata = props.item.metaData || props.item.meta_data || {}
      const orig = metadata.original_image_url
      if (!orig) return null
      const content = typeof orig === 'object' && orig.content ? orig.content : orig
      return typeof content === 'string' && content.length > 0 ? content : null
    })

    // Parse image gallery from metadata
    const imageGallery = computed(() => {
      const metadata = props.item.metaData || props.item.meta_data || {}
      const imagesData = metadata.images

      if (!imagesData) return []

      try {
        // Handle both object format { content: "[...]" } and direct string/array format
        const imagesContent = typeof imagesData === 'object' && imagesData.content
          ? imagesData.content
          : imagesData

        // Parse if it's a JSON string
        if (typeof imagesContent === 'string') {
          return JSON.parse(imagesContent)
        }

        // Return if it's already an array
        if (Array.isArray(imagesContent)) {
          return imagesContent
        }
      } catch (e) {
        console.warn('Failed to parse images metadata:', e)
      }

      return []
    })

    // Check if there's an image gallery with multiple images
    const hasImageGallery = computed(() => {
      return imageGallery.value.length > 1
    })

    // Get the current display image (gallery or default)
    const currentDisplayImage = computed(() => {
      if (imageLoadFailed.value) {
        return null
      }
      if (hasImageGallery.value) {
        return imageGallery.value[currentImageIndex.value] || imageGallery.value[0]
      }
      if (usingFallbackImage.value && originalImageUrl.value) {
        return originalImageUrl.value
      }
      // Use Reddit SVG as fallback for Reddit links without an image
      if (!props.item.image && isRedditUrl.value) {
        return '/reddit.svg'
      }
      // If no main image yet, try the original image URL from metadata
      if (!props.item.image && originalImageUrl.value) {
        return originalImageUrl.value
      }
      return props.item.image
    })

    const isRedditFallback = computed(() => {
      return !props.item.image && isRedditUrl.value
    })

    // Image hover handlers
    const onImageHover = () => {
      if (hasImageGallery.value) {
        isHoveringImage.value = true
      }
    }

    const onImageLeave = () => {
      isHoveringImage.value = false
      currentImageIndex.value = 0 // Reset to first image
    }

    const onImageMouseMove = (event) => {
      if (!hasImageGallery.value || !isHoveringImage.value) return

      const target = event.currentTarget
      const rect = target.getBoundingClientRect()
      const x = event.clientX - rect.left
      const width = rect.width
      const percentage = x / width

      // Calculate which image to show based on mouse position
      const imageCount = imageGallery.value.length
      const index = Math.floor(percentage * imageCount)
      currentImageIndex.value = Math.min(Math.max(0, index), imageCount - 1)
    }

    const onImageLoad = (event) => {
      // Lock the height to the first image in gallery, but only in masonry mode
      if (hasImageGallery.value && currentImageIndex.value === 0 && imageContainer.value) {
        // Check if we're in masonry mode by looking for the parent with masonry-mode class
        const isMasonryMode = imageContainer.value.closest('.masonry-mode')

        if (isMasonryMode) {
          const img = event.target
          if (img && img.naturalHeight) {
            firstImageHeight.value = img.naturalHeight
            // Set the container height to match the first image's rendered height plus padding (20px total)
            imageContainer.value.style.height = `${img.height + 20}px`
          }
        }
      }
    }

    // Touch event handlers for mobile swipe
    const onTouchStart = (event) => {
      if (!hasImageGallery.value || !isMobile.value) return
      
      const touch = event.touches[0]
      touchStartX.value = touch.clientX
      touchStartY.value = touch.clientY
      touchEndX.value = touch.clientX
      isSwiping.value = false
    }

    const onTouchMove = (event) => {
      if (!hasImageGallery.value || !isMobile.value) return
      
      const touch = event.touches[0]
      touchEndX.value = touch.clientX
      
      // Calculate the difference
      const diffX = Math.abs(touchEndX.value - touchStartX.value)
      const diffY = Math.abs(touch.clientY - touchStartY.value)
      
      // If horizontal movement is greater than vertical, we're swiping
      if (diffX > diffY && diffX > 10) {
        isSwiping.value = true
        event.preventDefault() // Prevent vertical scroll when swiping horizontally
      }
    }

    const onTouchEnd = () => {
      if (!hasImageGallery.value || !isMobile.value) return
      
      const diff = touchStartX.value - touchEndX.value
      const imageCount = imageGallery.value.length
      
      if (Math.abs(diff) >= swipeThreshold) {
        if (diff > 0) {
          // Swipe left - next image
          currentImageIndex.value = Math.min(currentImageIndex.value + 1, imageCount - 1)
        } else {
          // Swipe right - previous image
          currentImageIndex.value = Math.max(currentImageIndex.value - 1, 0)
        }
      }
      
      isSwiping.value = false
    }

    // Handle image click - don't open URL if we're swiping
    const handleImageClick = () => {
      if (isSwiping.value) return
      openUrl()
    }

    const getIconComponent = (iconName) => {
      if (!iconName) return LucideIcons.Tag
      const component = LucideIcons[iconName]
      return component || LucideIcons.Tag
    }

    const getActionIcon = (type) => {
      const icons = {
        'Watch': 'pi pi-play',
        'Read': 'pi pi-book',
        'Listen': 'pi pi-volume-up',
        'Play': 'pi pi-gamepad'
      }
      return icons[type] || 'pi pi-link'
    }

    const actionIcon = getActionIcon(props.item.actionType)

    const typeIconComponent = computed(() => {
      const type = props.item.type
      const mapping = {
        BOOK: LucideIcons.BookOpen,
        ARTICLE: LucideIcons.Newspaper,
        VIDEO: LucideIcons.Video,
        GAME: LucideIcons.Gamepad2,
        MOVIE: LucideIcons.Film,
        TV_SHOW: LucideIcons.Tv,
        RECIPE: LucideIcons.ChefHat,
        PRODUCT: LucideIcons.Package,
        PLACE: LucideIcons.MapPin,
        AUDIO: LucideIcons.Headphones,
      }
      return mapping[type] || LucideIcons.Link
    })

    // Helper to get extension from item
    const getExtension = () => {
      if (props.item.file_extension) return props.item.file_extension

      if (props.item.url) {
        try {
          const url = new URL(props.item.url)
          const pathname = url.pathname
          const lastDotIndex = pathname.lastIndexOf('.')
          if (lastDotIndex !== -1 && lastDotIndex < pathname.length - 1) {
            return pathname.substring(lastDotIndex + 1)
          }
        } catch (e) {
          // Fallback for non-standard URLs
          const lastDotIndex = props.item.url.lastIndexOf('.')
          if (lastDotIndex !== -1 && lastDotIndex < props.item.url.length - 1) {
            return props.item.url.substring(lastDotIndex + 1).split(/[?#]/)[0]
          }
        }
      }
      return ''
    }

    // File icon logic
    const shouldShowFileIcon = computed(() => {
      const ext = getExtension()
      return !!ext
    })

    const fileIcon = computed(() => {
      // Normalize extension: remove leading dot, trim whitespace, lowercase
      const rawExt = getExtension()
      const ext = (rawExt || '').toLowerCase().replace(/^\./, '').trim()

      // Map extensions to PrimeIcons classes
      const iconMap = {
        // Documents
        'pdf': 'pi pi-file-pdf icon-pdf',
        'doc': 'pi pi-file-word icon-word',
        'docx': 'pi pi-file-word icon-word',
        'txt': 'pi pi-file icon-text',
        'rtf': 'pi pi-file icon-text',
        'odt': 'pi pi-file-word icon-word',
        'md': 'pi pi-file icon-markdown',

        // Spreadsheets
        'xls': 'pi pi-file-excel icon-excel',
        'xlsx': 'pi pi-file-excel icon-excel',
        'csv': 'pi pi-file-excel icon-excel',
        'ods': 'pi pi-file-excel icon-excel',

        // Presentations
        'ppt': 'pi pi-file-powerpoint icon-powerpoint',
        'pptx': 'pi pi-file-powerpoint icon-powerpoint',
        'odp': 'pi pi-file-powerpoint icon-powerpoint',

        // Audio
        'mp3': 'pi pi-volume-up icon-audio',
        'wav': 'pi pi-volume-up icon-audio',
        'ogg': 'pi pi-volume-up icon-audio',
        'm4a': 'pi pi-volume-up icon-audio',
        'flac': 'pi pi-volume-up icon-audio',

        // Video
        'mp4': 'pi pi-video icon-video',
        'avi': 'pi pi-video icon-video',
        'mkv': 'pi pi-video icon-video',
        'mov': 'pi pi-video icon-video',
        'webm': 'pi pi-video icon-video',

        // Archives
        'zip': 'pi pi-box icon-archive',
        'rar': 'pi pi-box icon-archive',
        '7z': 'pi pi-box icon-archive',
        'tar': 'pi pi-box icon-archive',
        'gz': 'pi pi-box icon-archive',

        // Code
        'js': 'pi pi-code icon-code',
        'ts': 'pi pi-code icon-code',
        'html': 'pi pi-code icon-code',
        'css': 'pi pi-code icon-code',
        'py': 'pi pi-code icon-code',
        'java': 'pi pi-code icon-code',
        'c': 'pi pi-code icon-code',
        'cpp': 'pi pi-code icon-code',
        'json': 'pi pi-code icon-code',
        'xml': 'pi pi-code icon-code',

        // Images (fallback if type is not IMAGE but extension is image-like)
        'png': 'pi pi-image icon-image',
        'jpg': 'pi pi-image icon-image',
        'jpeg': 'pi pi-image icon-image',
        'gif': 'pi pi-image icon-image',
        'webp': 'pi pi-image icon-image',
        'svg': 'pi pi-image icon-image'
      }

      return iconMap[ext] || 'pi pi-file icon-default'
    })

    const formattedDate = props.item.dateAdded
      ? formatDate(props.item.dateAdded)
      : ''

    const openUrl = () => {
      if (props.item.url) {
        emit('open', props.item.id)
        safeOpenUrl(props.item.url)
      }
    }

    const shareLink = () => shareLinkViaWebShare(props.item)

    const openVaultVersion = () => {
      const vaultUrl = vaultMetaUrl(props.item)
      if (vaultUrl) safeOpenUrl(vaultUrl)
    }

    const onImgError = (e) => {
      if (!usingFallbackImage.value && originalImageUrl.value && props.item.image !== originalImageUrl.value) {
        // Main image failed (e.g. R2 not ready yet) — fall back to original image URL
        usingFallbackImage.value = true
        imageLoadFailed.value = false
      } else {
        imageLoadFailed.value = true
      }
    }

    const isYouTubeUrl = (url) => {
      if (!url) return false
      const urlString = url.toLowerCase()
      return urlString.includes('youtube.com') ||
        urlString.includes('youtu.be') ||
        urlString.includes('m.youtube.com')
    }

    // Twitter/X URL detection
    const twitterEmbedContainer = ref(null)

    const isTwitterUrl = computed(() => {
      if (!props.item.url) return false
      try {
        const url = new URL(props.item.url)
        const hostname = url.hostname.replace('www.', '')
        return (hostname === 'x.com' || hostname === 'twitter.com') && url.pathname.includes('/status/')
      } catch {
        return false
      }
    })

    // Reddit URL detection
    const isRedditUrl = computed(() => {
      if (!props.item.url) return false
      try {
        const hostname = new URL(props.item.url).hostname.replace('www.', '')
        return hostname === 'reddit.com' || hostname.endsWith('.reddit.com')
      } catch {
        return false
      }
    })

    // Extract tweet ID from URL
    const tweetId = computed(() => {
      if (!isTwitterUrl.value) return null
      try {
        const url = new URL(props.item.url)
        const match = url.pathname.match(/\/status\/(\d+)/)
        return match ? match[1] : null
      } catch {
        return null
      }
    })

    // Ensure Twitter widgets.js is loaded, returns a promise
    const ensureTwitterScript = () => {
      return new Promise((resolve) => {
        if (window.twttr && window.twttr.widgets) {
          resolve(window.twttr)
          return
        }

        // If script tag doesn't exist yet, create it
        if (!document.getElementById('twitter-wjs')) {
          const script = document.createElement('script')
          script.id = 'twitter-wjs'
          script.src = 'https://platform.twitter.com/widgets.js'
          script.async = true
          script.charset = 'utf-8'
          document.head.appendChild(script)
        }

        // Poll until twttr is ready
        const check = setInterval(() => {
          if (window.twttr && window.twttr.widgets) {
            clearInterval(check)
            resolve(window.twttr)
          }
        }, 100)
        setTimeout(() => clearInterval(check), 15000)
      })
    }

    // Render the tweet into the container
    let twitterRenderToken = 0
    let isComponentUnmounted = false
    const renderTweet = async (container) => {
      if (!container || !tweetId.value) return
      // Don't re-render if already has an iframe
      if (container.querySelector('iframe')) return

      const myToken = ++twitterRenderToken
      const twttr = await ensureTwitterScript()

      // Bail out if component unmounted, container detached, or a newer render started
      // during the async script load. Twitter's widgets.js performs internal appendChild
      // calls asynchronously and crashes ("parameter 1 is not of type 'Node'") if the
      // target container is no longer in the DOM.
      if (isComponentUnmounted || myToken !== twitterRenderToken || !container.isConnected) return

      const isDark = document.documentElement.classList.contains('dark-theme')
      container.innerHTML = ''
      try {
        twttr.widgets.createTweet(tweetId.value, container, {
          theme: isDark ? 'dark' : 'light',
          conversation: 'none',
          dnt: true
        })
      } catch (err) {
        console.warn('[LinkCard] Twitter embed render failed:', err)
      }
    }

    // Watch the ref — fires when v-if makes the element appear
    watch(twitterEmbedContainer, (el) => {
      if (el) renderTweet(el)
    })

    const truncatedUrl = computed(() => {
      if (!props.item.url) return ''
      const url = props.item.url
      const maxLength = 60

      // Remove protocol and www for display (always do this first)
      let displayUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '')

      // If still short enough, return as is
      if (displayUrl.length <= maxLength) return displayUrl

      // Truncate with ellipsis in the middle to preserve domain and end
      const start = displayUrl.substring(0, 25)
      const end = displayUrl.substring(displayUrl.length - 20)
      return `${start}...${end}`
    })

    // Check if item has an embed URL in metadata
    const hasEmbedUrl = computed(() => {
      const metadata = props.item.metaData || props.item.meta_data || {}
      const embedUrl = metadata.embed_url

      // Check if embed_url exists and has content
      if (!embedUrl) return false

      // Handle both object format { content: "url" } and string format
      const url = typeof embedUrl === 'object' ? embedUrl.content : embedUrl
      return !!url && url.trim().length > 0
    })

    // Extract custom user-defined metadata
    const customMetadata = computed(() => {
      // Pro-gated for the owner's own views, but always shown on public/shared/embed renders
      // (the metadata belongs to the Pro owner, not the viewer).
      if (!isPro.value && !props.publicView) return []
      
      const metadata = props.item.metaData || props.item.meta_data || {}
      const result = []

      // Check if we have address field (skip location if we do)
      const hasAddress = metadata.address && metadata.address.user_defined === true

      // Check if we have price field (skip currency if we do)
      const hasPrice = metadata.price && metadata.price.user_defined === true

      // Check if this is a PLACE type - if so, skip location and address as they're shown in BookmarkMetadata
      const isPlaceType = props.item.type === 'PLACE'

      Object.keys(metadata).forEach(key => {
        const item = metadata[key]

        // Skip location and address for PLACE type (already shown in BookmarkMetadata)
        if (isPlaceType && (key.toLowerCase() === 'location' || key.toLowerCase() === 'address')) {
          return
        }

        // Skip location if address exists (they belong together)
        if (key.toLowerCase() === 'location' && hasAddress) {
          return
        }

        // Skip currency if price exists (currency is part of price display)
        if (key.toLowerCase() === 'currency' && hasPrice) {
          return
        }

        if (item && typeof item === 'object' && item.user_defined === true) {
          const metaItem = {
            key: key,
            value: item,
            isPreset: ['price', 'rating', 'address'].includes(key.toLowerCase())
          }

          // For price, also include currency if available
          if (key.toLowerCase() === 'price' && metadata.currency) {
            metaItem.currency = metadata.currency
          }

          result.push(metaItem)
        }
      })

      return result
    })

    const formatMetadataValue = (item) => {
      if (!item || typeof item !== 'object') return String(item || '')

      const { content, type } = item

      if (content === null || content === undefined) return ''

      // Normalize type to uppercase for comparison
      const normalizedType = type ? type.toUpperCase() : 'TEXTUAL'

      switch (normalizedType) {
        case 'DATE':
          // Format date using utility function
          return formatDate(content)
        case 'NUMERICAL':
          // Format number
          return typeof content === 'number' ? content.toLocaleString() : String(content)
        case 'JSON':
          // Arrays/objects (e.g. provenance lists) — render readably, never "[object Object]"
          return formatJsonMetadata(content)
        case 'LOCATION':
        case 'TEXTUAL':
        default:
          // content can arrive as a parsed object/array even when the type is absent
          if (content && typeof content === 'object') return formatJsonMetadata(content)
          return String(content)
      }
    }

    // A TEXTUAL metadata value that is an http(s) URL is rendered as a clickable link
    const isLinkableMetadataValue = (item) => {
      return !!item && typeof item === 'object' && isHttpUrl(item.content)
    }

    const metadataUrl = (item) => String(item.content).trim()

    // Format price with currency (preset format)
    const formatPrice = (priceItem, currencyItem) => {
      if (!priceItem || !priceItem.content) return ''

      const price = priceItem.content
      const currency = currencyItem && currencyItem.content ? currencyItem.content : 'USD'

      // Format based on currency
      if (currency === 'EUR') {
        return `€${Number(price).toFixed(2)}`
      } else if (currency === 'GBP') {
        return `£${Number(price).toFixed(2)}`
      } else {
        return `$${Number(price).toFixed(2)}`
      }
    }

    // Format rating (preset format)
    const formatRating = (ratingItem) => {
      if (!ratingItem || !ratingItem.content) return ''

      const rating = Number(ratingItem.content)

      // If rating is between 0 and 1, treat as percentage
      if (rating > 0 && rating <= 1) {
        return `${Math.round(rating * 100)}%`
      }

      // Otherwise show as decimal
      return rating.toFixed(1)
    }

    // Format address (preset format)
    const formatAddress = (addressItem) => {
      if (!addressItem || !addressItem.content) return ''

      const content = addressItem.content

      // Handle JSON format { fullAddress: "..." }
      if (typeof content === 'object' && content.fullAddress) {
        return content.fullAddress
      }

      // Handle plain string format
      return String(content)
    }

    // A card whose save is parked in the offline queue (temp id, no longer
    // analyzing): the URL title and the "Still saving" refusals need a visible reason.
    const isAwaitingSync = computed(() => isPendingBookmarkId(props.item?.id) && !props.item?.isAnalyzing)
    // The English fallback only renders when the parent passed no translated label.
    // Bookmarks.vue always does, and no other view ever holds a pending card.
    const pendingSyncText = computed(() => props.pendingSyncLabel || 'Waiting to sync')
    // The image-only badge queues up after the other top-left badges.
    const pendingSyncBadgeLeft = computed(() => `${8 + (props.item?.reminder_at ? 30 : 0) + (props.hasPriceAlert ? 30 : 0)}px`)

    // Check if there's any content to display in item-content
    const hasAnyContent = computed(() => {
      // Check if title should be shown
      if (props.showTitle) return true

      // Check if URL should be shown
      if (props.showUrl && props.item.url) return true

      // Check if action icon or date should be shown
      if (props.showDate) return true

      // Reminder is an image overlay when the image is visible, so it does not
      // require a body content block in image-only mode.
      if (props.item.reminder_at && !props.showImage) return true

      // Same for the waiting-to-sync badge: with no image it needs the content block.
      if (isAwaitingSync.value && !props.showImage) return true

      // Check if tags should be shown and exist
      if (props.showTags && props.item.tags && props.item.tags.length > 0) return true

      // Check if automatic metadata should be shown and exists
      if (props.showAutomaticMetadata && props.item.type && props.item.type !== 'GENERIC') return true

      // Check if custom metadata should be shown and exists
      if (props.showCustomMetadata && customMetadata.value.length > 0) return true

      // Check if image description exists
      if (props.showAutomaticMetadata && imageDescription.value) return true

      // Check if notes should be shown and exist
      if (props.showNotes && props.item.notes) return true

      // No content to display
      return false
    })

    // Extract description from automatic metadata
    // We show description if file_type is IMAGE OR if type is GENERIC and it has an image file extension
    // This provides a fallback if file_type is missing but it's clearly an image
    const imageDescription = computed(() => {
      const metadata = props.item.metaData || props.item.meta_data || {}
      
      // Check file_type if available
      const fileType = props.item.file_type || props.item.fileType
      let isImage = fileType && String(fileType).toUpperCase() === 'IMAGE'

      if (!isImage) return ''
      
      const desc = metadata.description
      
      if (!desc) return ''
      
      // Handle object format { content: "...", type: "TEXTUAL" }
      if (typeof desc === 'object' && desc.content) {
        return String(desc.content)
      }
      
      // Handle plain string
      if (typeof desc === 'string') {
        return desc
      }
      
      return ''
    })

    const reminderTooltip = computed(() => {
      if (!props.item.reminder_at) return ''
      return `Reminder: ${formatDateTime(props.item.reminder_at)}`
    })

    // Sanitize config for notes: allow hosted images from img.linkflare.io
    const notesSanitizeConfig = {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr', 'blockquote', 'pre', 'code',
        'ul', 'ol', 'li',
        'a', 'img',
        'strong', 'em', 'u', 's', 'mark',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'div', 'span'
      ]
    }

    // Strip non-whitelisted image sources (base64 junk, external hotlinks).
    // Host is centralized in constants.js (L6); regex hoisted to module scope.
    const stripNonHostedImages = (html) => {
      return html.replace(NON_HOSTED_IMAGE_REGEX, '')
    }

    // Format notes content - handle both HTML from Quill and plain text/markdown
    const formattedNotes = computed(() => {
      if (!props.item.notes) return ''

      const notes = props.item.notes

      // Check if it's already HTML (contains HTML tags like <p>, <div>, etc.)
      if (/<[a-z][\s\S]*>/i.test(notes)) {
        return stripNonHostedImages(sanitizeHtml(notes, notesSanitizeConfig))
      }

      // If it's plain text or markdown, convert to HTML using the markdown converter
      // and sanitize the result
      return stripNonHostedImages(sanitizeHtml(markdownToHtml(notes), notesSanitizeConfig))
    })

    // Get rating tooltip based on type
    const getRatingTooltip = (ratingType) => {
      const type = props.item.type

      const tooltips = {
        PLACE: {
          UP: 'I went and liked this place',
          DOWN: 'I went and didn\'t like this place'
        },
        GAME: {
          UP: 'I enjoyed playing this game',
          DOWN: 'I didn\'t enjoy playing this game'
        },
        MOVIE: {
          UP: 'I enjoyed this movie',
          DOWN: 'I didn\'t enjoy this movie'
        },
        TV_SHOW: {
          UP: 'I enjoyed this show',
          DOWN: 'I didn\'t enjoy this show'
        },
        BOOK: {
          UP: 'I enjoyed this book',
          DOWN: 'I didn\'t enjoy this book'
        },
        ARTICLE: {
          UP: 'I found this article helpful',
          DOWN: 'I didn\'t find this article helpful'
        },
        RECIPE: {
          UP: 'I enjoyed this recipe',
          DOWN: 'I didn\'t enjoy this recipe'
        },
        VIDEO: {
          UP: 'I enjoyed this video',
          DOWN: 'I didn\'t enjoy this video'
        },
        PRODUCT: {
          UP: 'I like this product',
          DOWN: 'I don\'t like this product'
        }
      }

      // Default for GENERIC or unknown types
      const defaultTooltips = {
        UP: 'I like this',
        DOWN: 'I don\'t like this'
      }

      return (tooltips[type] || defaultTooltips)[ratingType]
    }

    // Handle rating button clicks
    const handleRatingClick = (ratingType) => {
      
      // Determine the new rating
      let newRating = ratingType

      // If clicking the same rating that's already set, clear it
      if (props.item.rating === ratingType) {
        newRating = 'NONE'
      }

      
      // Emit the rate event to parent
      emit('rate', props.item.id, newRating)
    }

    // Mobile menu items
    // Cached AI article summary bullets (Pro-only; generated server-side and stored in metadata).
    const articleSummaryBullets = computed(() => {
      const md = props.item.metaData || props.item.meta_data || {}
      const content = md.ai_summary && md.ai_summary.content
      if (content && Array.isArray(content.bullets)) {
        return content.bullets.filter(b => b && String(b).trim().length > 0)
      }
      return []
    })

    const mobileMenuItems = computed(() => {
      const items = [
        {
          label: 'Open link',
          icon: 'pi pi-external-link',
          command: () => openUrl()
        }
      ]

      if (props.item.url) {
        items.push({
          label: 'Share',
          icon: 'pi pi-share-alt',
          command: () => shareLink()
        })
      }

      if (props.item.type === 'ARTICLE') {
        items.push({
          label: 'Read article',
          icon: 'pi pi-book',
          command: () => emit('read', props.item.id)
        })
        items.push({
          label: 'See summary',
          icon: 'pi pi-sparkles',
          command: () => emit('see-summary', props.item)
        })
      }

      if (props.item.type === 'VIDEO' && hasEmbedUrl.value) {
        items.push({
          label: 'Watch video',
          icon: 'pi pi-play',
          command: () => emit('watch', props.item)
        })
      }

      if ((props.item.vaultId || props.item.vault_id) && isPro.value) {
        items.push({
          label: 'View Vault Version',
          icon: 'pi pi-lock',
          command: () => openVaultVersion()
        })
      }

      items.push(
        { separator: true },
        {
          label: getRatingTooltip('UP'),
          icon: 'pi pi-thumbs-up',
          command: () => handleRatingClick('UP'),
          class: props.item.rating === 'UP' ? 'rated-up' : ''
        },
        {
          label: getRatingTooltip('DOWN'),
          icon: 'pi pi-thumbs-down',
          command: () => handleRatingClick('DOWN'),
          class: props.item.rating === 'DOWN' ? 'rated-down' : ''
        }
      )

      if (props.item.rating && props.item.rating !== 'NONE') {
        items.push({
          label: 'Clear rating',
          icon: 'pi pi-times-circle',
          command: () => handleRatingClick('NONE')
        })
      }

      items.push(
        { separator: true },
      )
      if (canEdit.value) {
        items.push({
          label: 'Edit',
          icon: 'pi pi-pencil',
          command: () => emit('edit', props.item)
        })
      }
      if (canDelete.value) {
        items.push({
          label: 'Delete',
          icon: 'pi pi-trash',
          class: 'danger-item',
          command: () => emit('delete', props.item.id)
        })
      }

      return items
    })

    // Toggle mobile menu
    const toggleMobileMenu = (event) => {
      // On a phone the ⋮ button and a long-press mean the same thing: open the
      // quick-actions sheet. The PrimeVue popup menu stays for desktop's
      // narrow-card fallback and for surfaces without quick actions.
      if (gesturesEnabled()) {
        emit('quick-actions', { item: props.item, source: 'menu' })
        return
      }
      if (mobileMenu.value) {
        mobileMenu.value.toggle(event)
      }
    }

    // Cleanup
    onBeforeUnmount(() => {
      isComponentUnmounted = true

      // Clean up ResizeObserver
      if (cardElement.value && cardElement.value._resizeObserver) {
        cardElement.value._resizeObserver.disconnect()
        delete cardElement.value._resizeObserver
      }

      // Clean up the class-change MutationObserver (H7)
      if (classMutationObserver) {
        classMutationObserver.disconnect()
        classMutationObserver = null
      }
    })

    return {
      formatDate,
      formatDateTime,
      isPro,
      highlightCount,
      canEdit,
      canDelete,
      shouldShowFileIcon,
      fileIcon,
      getIconComponent,
      actionIcon,
      typeIconComponent,
      formattedDate,
      openUrl,
      openVaultVersion,
      onImgError,
      isYouTubeUrl,
      isTwitterUrl,
      twitterEmbedContainer,
      truncatedUrl,
      hasEmbedUrl,
      isOfflineAvailable,
      readingProgress,
      customMetadata,
      articleSummaryBullets,
      formatAttributeName,
      formatMetadataValue,
      isLinkableMetadataValue,
      metadataUrl,
      formatPrice,
      formatRating,
      formatAddress,
      hasAnyContent,
      isAwaitingSync,
      pendingSyncText,
      pendingSyncBadgeLeft,
      formattedNotes,
      imageGallery,
      hasImageGallery,
      currentDisplayImage,
      isRedditFallback,
      currentImageIndex,
      isHoveringImage,
      onImageHover,
      onImageLeave,
      onImageMouseMove,
      onImageLoad,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onCardTouchStart,
      onCardTouchMove,
      onCardTouchEnd,
      cardSwipeOffset,
      cardSwipeStyle,
      swipeRightAction,
      swipeLeftAction,
      swipeArmed,
      onCardTouchCancel,
      onCardContextMenu,
      isCardSwiping,
      handleImageClick,
      imageContainer,
      cardElement,
      firstImageHeight,
      getRatingTooltip,
      handleRatingClick,
      isMobile,
      isNarrowCard,
      mobileMenu,
      mobileMenuItems,
      toggleMobileMenu,
      isAnimatingArchive,
      isAnimatingDelete,
      reminderTooltip,
      imageDescription
    }
  }
}
</script>

<style scoped>
/* ===== Type Color Variables (Refined Palette) ===== */
.link-card {
  --type-article: #5b7fc7;
  --type-article-light: rgba(91, 127, 199, 0.08);
  --type-video: #e53e3e;
  --type-video-light: rgba(229, 62, 62, 0.08);
  --type-book: #9f5cc0;
  --type-book-light: rgba(159, 92, 192, 0.08);
  --type-recipe: #2d9d5c;
  --type-recipe-light: rgba(45, 157, 92, 0.08);
  --type-place: #d857d5;
  --type-place-light: rgba(216, 87, 213, 0.08);
  --type-product: #14b8a6;
  --type-product-light: rgba(20, 184, 166, 0.08);
  --type-movie: #eab308;
  --type-movie-light: rgba(234, 179, 8, 0.08);
  --type-tv_show: #f97316;
  --type-tv_show-light: rgba(249, 115, 22, 0.08);
  --type-game: #333333;
  --type-game-light: rgba(51, 51, 51, 0.08);
  --type-podcast: #ec4899;
  --type-podcast-light: rgba(236, 72, 153, 0.08);
  --type-generic: #94a3b8;
  --type-generic-light: rgba(148, 163, 184, 0.08);
}

.link-card {
  background: var(--bg-secondary);
  border-radius: 16px;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.04),
    0 4px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  /* Floor card height so skeleton → real-card swap doesn't reshuffle
     grid rows (each row is sized to its tallest card, so a content-
     height variance caused a CLS cluster on /bookmarks). Matches
     LinkCardSkeleton.min-height. List-mode overrides further below. */
  min-height: 380px;
  box-sizing: border-box;
  content-visibility: auto;
  contain-intrinsic-size: auto 400px;
  contain: layout style paint;
  will-change: transform;
}

.link-card.is-list {
  min-height: 0;
}

/* Image-only mode: card has no text content, so drop the CLS floor and
   let the card collapse to the image's natural height. Without this,
   grid mode shows a large empty strip below short images. */
.link-card.is-image-only {
  min-height: 0;
  contain-intrinsic-size: auto;
}
.link-card.is-image-only:not(.is-list) .item-image {
  height: auto;
  min-height: 0;
}

/* Pre-rendered hover shadow on ::after — only transitions opacity (compositor-only, zero repaint) */
.link-card::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 16px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.1),
    0 8px 24px rgba(0, 0, 0, 0.08);
  opacity: 0;
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  z-index: 0;
}

:root.dark-theme .link-card {
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.2),
    0 4px 12px rgba(0, 0, 0, 0.15);
}

:root.dark-theme .link-card::after {
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.3),
    0 8px 24px rgba(0, 0, 0, 0.2);
}

.link-card:hover {
  transform: translateY(-2px);
}

.link-card:hover::after {
  opacity: 1;
}

/* ===== Type-Based Top Border Accent ===== */
.link-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: var(--type-generic);
  z-index: 10;
  border-radius: 16px 16px 0 0;
  transition: height 0.3s ease;
}

.link-card:hover::before {
  height: 8px;
}

/* Type-specific border colors */
.link-card.type-article::before {
  background: linear-gradient(90deg, var(--type-article), color-mix(in srgb, var(--type-article) 80%, white));
}
.link-card.type-video::before {
  background: linear-gradient(90deg, var(--type-video), color-mix(in srgb, var(--type-video) 80%, white));
}
.link-card.type-book::before {
  background: linear-gradient(90deg, var(--type-book), color-mix(in srgb, var(--type-book) 80%, white));
}
.link-card.type-recipe::before {
  background: linear-gradient(90deg, var(--type-recipe), color-mix(in srgb, var(--type-recipe) 80%, white));
}
.link-card.type-place::before {
  background: linear-gradient(90deg, var(--type-place), color-mix(in srgb, var(--type-place) 80%, white));
}
.link-card.type-product::before {
  background: linear-gradient(90deg, var(--type-product), color-mix(in srgb, var(--type-product) 80%, white));
}
.link-card.type-movie::before {
  background: linear-gradient(90deg, var(--type-movie), color-mix(in srgb, var(--type-movie) 80%, white));
}
.link-card.type-tv_show::before {
  background: linear-gradient(90deg, var(--type-tv_show), color-mix(in srgb, var(--type-tv_show) 80%, white));
}
.link-card.type-game::before {
  background: linear-gradient(90deg, var(--type-game), color-mix(in srgb, var(--type-game) 80%, white));
}
.link-card.type-podcast::before {
  background: linear-gradient(90deg, var(--type-podcast), color-mix(in srgb, var(--type-podcast) 80%, white));
}

/* Hide link type border when disabled */
.link-card.hide-link-type::before {
  display: none;
}

/* Image area */
.item-image {
  height: 200px;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

/* Subtle inner vignette effect */
.item-image::after {
  content: '';
  position: absolute;
  inset: 0;
  box-shadow: inset 0 0 40px rgba(0, 0, 0, 0.20);
  pointer-events: none;
  z-index: 2;
}

.item-image.clickable {
  cursor: pointer;
}

.item-image.clickable:hover img {
  transform: scale(1.02);
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block; /* remove inline-baseline gap that adds a strip below the image */
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
}

/* Image gallery cursor hint */
.item-image.has-gallery {
  cursor: ew-resize;
}

/* Gallery images after the first one use contain to prevent jumping */
.item-image img.gallery-contain {
  object-fit: contain !important;
}

/* Gallery indicators */
.gallery-indicators {
  position: absolute;
  bottom: 56px; /* Moved up to avoid overlapping with action buttons on desktop */
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(15, 23, 42, 0.88);
  border-radius: 20px;
  z-index: 5;
  animation: fadeInUp 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* On mobile, gallery indicators can be at the bottom since action buttons are in a menu */
@media (max-width: 768px) {
  .gallery-indicators {
    bottom: 12px;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* Mobile gallery indicators - always visible */
.gallery-indicators.mobile-visible {
  animation: none;
  opacity: 1;
}

.gallery-indicator-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.gallery-indicator-dot.active {
  background: white;
  transform: scale(1.4);
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.9);
}

/* Mobile swipe hint */
@media (max-width: 768px) {
  .item-image.has-gallery {
    cursor: grab;
    touch-action: pan-y pinch-zoom; /* Allow vertical scroll but handle horizontal swipe */
  }
  
  .item-image.has-gallery:active {
    cursor: grabbing;
  }
}

/* Book covers should use contain to preserve aspect ratio */
.link-card.type-book .item-image {
  background: var(--bg-secondary);
}

.link-card.type-book .item-image img {
  object-fit: contain;
}

/* Movie posters should also use contain to preserve aspect ratio */
.link-card.type-movie .item-image {
  background: var(--bg-secondary);
}

.link-card.type-movie .item-image img {
  object-fit: contain;
}

/* TV Show posters should also use contain to preserve aspect ratio */
.link-card.type-tv_show .item-image {
  background: var(--bg-secondary);
}

.link-card.type-tv_show .item-image img {
  object-fit: contain;
}

/* Product images should use contain to preserve aspect ratio */
.link-card.type-product .item-image {
  background: var(--bg-secondary);
}

.link-card.type-product .item-image img {
  object-fit: contain;
}

.placeholder-image {
  font-size: 3rem;
  color: var(--text-secondary);
  opacity: 0.5;
}

.youtube-indicator {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(229, 62, 62, 0.95);
  color: white;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.85rem;
  box-shadow: 0 2px 8px rgba(229, 62, 62, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 5;
}

.youtube-indicator i {
  margin: 0;
}

.offline-available-indicator {
  position: absolute;
  top: 12px;
  left: 12px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.95), rgba(139, 92, 246, 0.95));
  color: white;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.85rem;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  z-index: 5;
  gap: 4px;
}

.offline-available-indicator i {
  margin: 0;
  font-size: 0.85rem;
}

.pinned-indicator {
  position: absolute;
  top: 12px;
  left: 12px;
  background: linear-gradient(135deg, rgba(244, 114, 182, 0.95), rgba(236, 72, 153, 0.95));
  color: white;
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 0.85rem;
  box-shadow: 0 2px 8px rgba(236, 72, 153, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  z-index: 5;
}

.pinned-indicator i {
  margin: 0;
  font-size: 0.85rem;
}

.link-type-indicator {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(30, 41, 59, 0.92);
  color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.link-type-indicator:hover {
  background: rgba(30, 41, 59, 0.95);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

/* Type-specific background colors for the indicator */
.link-card.type-article .link-type-indicator { background-color: var(--type-article); }
.link-card.type-video .link-type-indicator { background-color: var(--type-video); }
.link-card.type-book .link-type-indicator { background-color: var(--type-book); }
.link-card.type-recipe .link-type-indicator { background-color: var(--type-recipe); }
.link-card.type-place .link-type-indicator { background-color: var(--type-place); }
.link-card.type-product .link-type-indicator { background-color: var(--type-product); }
.link-card.type-movie .link-type-indicator { background-color: var(--type-movie); }
.link-card.type-tv_show .link-type-indicator { background-color: var(--type-tv_show); }
.link-card.type-game .link-type-indicator { background-color: var(--type-game); }
.link-card.type-podcast .link-type-indicator { background-color: var(--type-podcast); }
.link-card.type-generic .link-type-indicator { background-color: var(--type-generic); }

/* Hover states for type-specific indicators - slightly darken */
.link-card.type-article .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-article), black 10%); }
.link-card.type-video .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-video), black 10%); }
.link-card.type-book .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-book), black 10%); }
.link-card.type-recipe .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-recipe), black 10%); }
.link-card.type-place .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-place), black 10%); }
.link-card.type-product .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-product), black 10%); }
.link-card.type-movie .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-movie), black 10%); }
.link-card.type-tv_show .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-tv_show), black 10%); }
.link-card.type-game .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-game), black 10%); }
.link-card.type-podcast .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-podcast), black 10%); }
.link-card.type-generic .link-type-indicator:hover { background-color: color-mix(in srgb, var(--type-generic), black 10%); }

/* If reading progress is present (which is at right: 12px), move this one to the left */
.link-type-indicator.has-reading-progress {
  right: 56px;
}

.reading-progress-indicator {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 50%;
  padding: 5px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  transition: background 0.3s ease, box-shadow 0.3s ease;
  z-index: 5;
}

.reading-progress-indicator.completed {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 3px 16px rgba(99, 102, 241, 0.5);
  animation: completedPulse 2s ease-in-out infinite;
}

@keyframes completedPulse {

  0%,
  100% {
    box-shadow: 0 3px 12px rgba(102, 126, 234, 0.4);
  }

  50% {
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.6);
  }
}

.progress-ring {
  position: relative;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-ring svg {
  transform: rotate(-90deg);
  width: 32px;
  height: 32px;
}

.progress-ring-circle-bg {
  fill: none;
  stroke: #e0e0e0;
  stroke-width: 2;
}

.progress-ring-circle-filled {
  fill: url(#progressGradient);
  stroke: url(#progressGradient);
  stroke-width: 3;
}

.progress-ring-circle {
  fill: none;
  stroke: url(#progressGradient);
  stroke-width: 3;
  stroke-dasharray: 88;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.3s ease;
}

.progress-text {
  position: absolute;
  font-size: 9px;
  font-weight: 700;
  color: #667eea;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  transition: color 0.3s ease, font-size 0.3s ease;
}

.progress-text.completed {
  color: white;
  font-size: 14px;
}

/* SVG gradient for progress ring */
:global(body) {
  --progress-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Action buttons overlay */
.item-actions-overlay {
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  gap: 0.35rem;
  opacity: 0;
  transition: opacity 0.25s ease, transform 0.25s ease;
  z-index: 10;
  transform: translateY(4px);
}

.link-card:hover .item-actions-overlay {
  opacity: 1;
  transform: translateY(0);
}

.item-actions-overlay .action-button {
  background: rgba(255, 255, 255, 0.97) !important;
  border-radius: 10px;
  padding: 0.55rem !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 38px;
}

.item-actions-overlay .action-button:hover {
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  transform: translateY(-2px);
}

.item-actions-overlay .action-button.p-button-danger:hover {
  background: rgba(239, 68, 68, 0.95) !important;
  color: white !important;
  border-color: rgba(239, 68, 68, 0.3);
}

/* Compact menu button for narrow cards */
.item-actions-overlay .compact-menu-button {
  background: rgba(255, 255, 255, 0.97) !important;
  border: 1px solid rgba(255, 255, 255, 0.8);
}

.item-actions-overlay .compact-menu-button:hover {
  background: rgba(255, 255, 255, 1) !important;
  transform: translateY(-2px);
}

/* Vault button styling */
.item-actions-overlay .action-button.vault-button {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%) !important;
  color: white !important;
}

.item-actions-overlay .action-button.vault-button:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(139, 92, 246, 1) 100%) !important;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

/* Dark theme for action buttons */
:root.dark-theme .item-actions-overlay .action-button {
  background: rgba(30, 30, 30, 0.95) !important;
  color: var(--text-primary) !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
}

:root.dark-theme .item-actions-overlay .action-button:hover {
  background: rgba(45, 45, 45, 1) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
}

:root.dark-theme .item-actions-overlay .compact-menu-button {
  background: rgba(30, 30, 30, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-primary) !important;
}

:root.dark-theme .item-actions-overlay .compact-menu-button:hover {
  background: rgba(45, 45, 45, 1) !important;
}

/* Dark theme vault button keeps its gradient */
:root.dark-theme .item-actions-overlay .action-button.vault-button {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%) !important;
  color: white !important;
}

:root.dark-theme .item-actions-overlay .action-button.vault-button:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(139, 92, 246, 1) 100%) !important;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.5);
}

/* Rating buttons overlay - bottom left */
.item-rating-overlay {
  position: absolute;
  bottom: 12px;
  left: 12px;
  display: flex;
  gap: 0.35rem;
  opacity: 0;
  transition: opacity 0.25s ease, transform 0.25s ease;
  z-index: 10;
  transform: translateY(4px);
}

.link-card:hover .item-rating-overlay {
  opacity: 1;
  transform: translateY(0);
}

.item-rating-overlay .rating-button {
  background: rgba(255, 255, 255, 0.97) !important;
  border-radius: 10px;
  padding: 0.45rem !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--text-secondary) !important;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(255, 255, 255, 0.8);
}

.item-rating-overlay .rating-button:hover {
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  transform: translateY(-2px) scale(1.05);
  color: var(--text-primary) !important;
}

.item-rating-overlay .rating-button.rated {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.item-rating-overlay .rating-button.rated:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(139, 92, 246, 1) 100%) !important;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.5);
}

/* Dark theme for rating buttons */
:root.dark-theme .item-rating-overlay .rating-button {
  background: rgba(30, 30, 30, 0.95) !important;
  color: var(--text-secondary) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
}

:root.dark-theme .item-rating-overlay .rating-button:hover {
  background: rgba(45, 45, 45, 1) !important;
  color: var(--text-primary) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
}

:root.dark-theme .item-rating-overlay .rating-button.rated {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%) !important;
  color: white !important;
}

:root.dark-theme .item-rating-overlay .rating-button.rated:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(139, 92, 246, 1) 100%) !important;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.5);
}

/* Action and Rating buttons overlay when no image (appears on hover) */
.content-actions-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
  pointer-events: none;
}

.link-card:hover .content-actions-overlay {
  opacity: 1;
  pointer-events: auto;
}

.content-rating-buttons {
  display: flex;
  gap: 0.25rem;
}

.content-action-buttons {
  display: flex;
  gap: 0.25rem;
}

.content-actions-overlay .rating-button {
  background: rgba(255, 255, 255, 0.97) !important;
  border-radius: 4px;
  padding: 0.4rem !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  color: var(--text-secondary) !important;
  width: 37px;
  height: 37px;
}

.content-actions-overlay .rating-button:hover {
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transform: translateY(-1px);
  color: var(--text-primary) !important;
}

.content-actions-overlay .rating-button.rated {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%) !important;
  color: white !important;
}

.content-actions-overlay .rating-button.rated:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(139, 92, 246, 1) 100%) !important;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.content-actions-overlay .action-button {
  background: rgba(255, 255, 255, 0.97) !important;
  border-radius: 4px;
  padding: 0.5rem !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.content-actions-overlay .action-button:hover {
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transform: translateY(-1px);
}

.content-actions-overlay .action-button.p-button-danger:hover {
  background: rgba(239, 68, 68, 0.95) !important;
  color: white !important;
}

.content-actions-overlay .action-button.vault-button {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%) !important;
  color: white !important;
}

.content-actions-overlay .action-button.vault-button:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(139, 92, 246, 1) 100%) !important;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.content-actions-overlay .compact-menu-button {
  background: rgba(255, 255, 255, 0.97) !important;
}

.content-actions-overlay .compact-menu-button:hover {
  background: rgba(255, 255, 255, 1) !important;
}

/* Dark theme for content actions overlay */
:root.dark-theme .content-actions-overlay .rating-button {
  background: rgba(30, 30, 30, 0.95) !important;
  color: var(--text-secondary) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

:root.dark-theme .content-actions-overlay .rating-button:hover {
  background: rgba(45, 45, 45, 1) !important;
  color: var(--text-primary) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

:root.dark-theme .content-actions-overlay .rating-button.rated {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%) !important;
  color: white !important;
}

:root.dark-theme .content-actions-overlay .rating-button.rated:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(139, 92, 246, 1) 100%) !important;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.5);
}

:root.dark-theme .content-actions-overlay .action-button {
  background: rgba(30, 30, 30, 0.95) !important;
  color: var(--text-primary) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

:root.dark-theme .content-actions-overlay .action-button:hover {
  background: rgba(45, 45, 45, 1) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

:root.dark-theme .content-actions-overlay .compact-menu-button {
  background: rgba(30, 30, 30, 0.95) !important;
  color: var(--text-primary) !important;
}

:root.dark-theme .content-actions-overlay .compact-menu-button:hover {
  background: rgba(45, 45, 45, 1) !important;
}

:root.dark-theme .content-actions-overlay .action-button.vault-button {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%) !important;
  color: white !important;
}

:root.dark-theme .content-actions-overlay .action-button.vault-button:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(139, 92, 246, 1) 100%) !important;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.5);
}

/* Mobile three-dot menu button */
.mobile-menu-button {
  position: absolute !important;
  bottom: 8px !important;
  right: 8px !important;
  top: auto !important;
  left: auto !important;
  background: rgba(255, 255, 255, 0.97) !important;
  border-radius: 6px !important;
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  max-width: 32px !important;
  min-height: 32px !important;
  max-height: 32px !important;
  padding: 0 !important;
  margin: 0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
  display: none;
  flex-shrink: 0;
}

.mobile-menu-button.no-image {
  top: 8px !important;
  right: 8px !important;
  bottom: auto !important;
}

/* Show mobile menu button only on mobile */
@media (max-width: 768px) {
  .mobile-menu-button {
    display: flex !important;
    align-items: center;
    justify-content: center;
  }

  /* Hide desktop action and rating overlays on mobile */
  .item-actions-overlay,
  .item-rating-overlay {
    display: none !important;
  }
}

/* Dark theme for mobile menu button */
:root.dark-theme .mobile-menu-button {
  background: rgba(30, 30, 30, 0.95) !important;
  color: var(--text-primary) !important;
}

/* Mobile menu styling */
:deep(.mobile-actions-menu) {
  max-width: 280px;
}

:deep(.mobile-actions-menu .p-menuitem-link) {
  padding: 0.75rem 1rem;
}

:deep(.mobile-actions-menu .p-menuitem.rated-up .p-menuitem-link) {
  background: var(--accent-light);
  color: var(--accent-color);
}

:deep(.mobile-actions-menu .p-menuitem.rated-up .p-menuitem-link .p-menuitem-icon) {
  color: var(--accent-color);
}

:deep(.mobile-actions-menu .p-menuitem.rated-down .p-menuitem-link) {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

:deep(.mobile-actions-menu .p-menuitem.rated-down .p-menuitem-link .p-menuitem-icon) {
  color: #ef4444;
}

:deep(.mobile-actions-menu .p-menuitem.danger-item .p-menuitem-link) {
  color: #ef4444;
}

:deep(.mobile-actions-menu .p-menuitem-link .p-menuitem-icon) {
  color: var(--accent-color);
}

:deep(.mobile-actions-menu .p-menuitem.danger-item .p-menuitem-link .p-menuitem-icon) {
  color: #ef4444;
}

:deep(.mobile-actions-menu .p-menuitem.danger-item:hover .p-menuitem-link) {
  background: rgba(239, 68, 68, 0.1);
}

/* Content */
.file-icon-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  gap: 0.5rem;
}

img.reddit-fallback {
  max-width: 150px !important;
  width: auto !important;
  height: auto !important;
  object-fit: contain;
  padding: 2rem;
  margin: auto;
}

.image-icon-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  opacity: 0.6;
}

.file-extension {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  padding: 2px 6px;
  border-radius: 4px;
}

/* File Icon Colors */
.icon-pdf {
  color: #e11d48;
}

/* Red */
.icon-word {
  color: #2563eb;
}

/* Blue */
.icon-excel {
  color: #16a34a;
}

/* Green */
.icon-powerpoint {
  color: #ea580c;
}

/* Orange */
.icon-image {
  color: #9333ea;
}

/* Purple */
.icon-video {
  color: #db2777;
}

/* Pink */
.icon-audio {
  color: #0891b2;
}

/* Cyan */
.icon-archive {
  color: #d97706;
}

/* Amber */
.icon-code {
  color: #4b5563;
}

/* Gray */
.icon-text {
  color: #64748b;
}

/* Slate */
.icon-default {
  color: #94a3b8;
}

/* Light Gray */

.item-content {
  padding: 1rem;
}

.item-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.3;
  width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  max-height: 2.6em;
  overflow: hidden;
}

.item-title.clickable {
  cursor: pointer;
  transition: color 0.2s ease;
}

.item-title.clickable:hover {
  color: var(--accent-color);
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.item-date {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.item-url {
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0 0 0.5rem 0;
  word-break: break-all;
}

.analyzing-state {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  color: var(--primary-color);
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.8;
}

.analyzing-skeleton {
  border-radius: 8px;
}

/* Save parked in the offline queue. Same slot as .analyzing-state. No fill: text-secondary
   needs the card's own background for AA (4.69:1 light on #fff, 5.62:1 dark). */
.pending-sync-state {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: fit-content;
  max-width: 100%;
  margin: 0 0 0.5rem 0;
  padding: 0.125rem 0.5rem;
  border: 1px dashed var(--text-secondary);
  border-radius: 999px;
  color: var(--text-secondary);
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.4;
}

.pending-sync-state svg {
  flex-shrink: 0;
}

.item-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag-small {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.item-knowledge-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.375rem;
}

.knowledge-card-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  border-radius: 4px;
  background: color-mix(in srgb, var(--accent-color, #1976d2) 15%, transparent);
  color: var(--accent-color, #1976d2);
  border: 1px solid var(--accent-color, #1976d2);
  text-decoration: none;
  max-width: 200px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  transition: background 0.15s;
  cursor: pointer;
}

.knowledge-card-chip:hover {
  background: color-mix(in srgb, var(--accent-color, #1976d2) 25%, transparent);
}

.knowledge-card-chip i {
  font-size: 12px;
  flex-shrink: 0;
}

.knowledge-card-chip span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Twitter/X embed section */
.twitter-embed-section {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-color);
  overflow: hidden;
  border-radius: 8px;
}

.twitter-embed-section .twitter-tweet {
  margin: 0 !important;
}

.twitter-embed-section iframe {
  border-radius: 8px !important;
}

/* Custom metadata section */
.custom-metadata-section {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Image description section */
.image-description-section {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-color);
}

.image-description-text {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.custom-metadata-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  background: var(--bg-primary);
  padding: 0.5rem;
  border-radius: 4px;
}

/* Preset metadata items display inline with icon */
.custom-metadata-item.preset-metadata {
  flex-direction: row;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  flex-wrap: wrap;
  min-width: 0;
}

.custom-metadata-item.preset-metadata svg {
  flex-shrink: 0;
  color: var(--text-secondary);
  margin-top: 2px;
}

.custom-metadata-item.preset-metadata .metadata-value {
  font-size: 0.8125rem;
  font-weight: 500;
  min-width: 0;
  flex: 1;
}

.custom-metadata-item .metadata-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.custom-metadata-item .metadata-value {
  font-size: 0.875rem;
  color: var(--text-primary);
  word-break: break-word;
  overflow-wrap: anywhere;
  min-width: 0;
}

.custom-metadata-item a.metadata-link {
  color: var(--accent-color);
  text-decoration: none;
}

.custom-metadata-item a.metadata-link:hover {
  text-decoration: underline;
}

/* Notes section */
.notes-section {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-color);
}

.article-summary-section {
  margin-top: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: var(--accent-light);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.article-summary-heading {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--accent-color);
  margin-bottom: 0.4rem;
}

.article-summary-list {
  margin: 0;
  padding-left: 1.1rem;
  list-style: disc;
}

.article-summary-list li {
  font-size: 0.825rem;
  line-height: 1.5;
  color: var(--text-primary);
  margin-bottom: 0.2rem;
}

.article-summary-list li:last-child {
  margin-bottom: 0;
}

.notes-content {
  font-size: 0.875rem;
  color: var(--text-primary);
  line-height: 1.6;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
  background: var(--bg-primary);
  border-radius: 4px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Reset and style all possible HTML elements from Quill editor */
.notes-content :deep(*) {
  max-width: 100%;
}

.notes-content :deep(p) {
  margin: 0 0 0.5rem 0;
  line-height: 1.6;
}

.notes-content :deep(p:last-child) {
  margin-bottom: 0;
}

.notes-content :deep(h1),
.notes-content :deep(h2),
.notes-content :deep(h3) {
  margin: 0.75rem 0 0.5rem 0;
  font-weight: 600;
  line-height: 1.3;
}

.notes-content :deep(h1) {
  font-size: 1.25em;
}

.notes-content :deep(h2) {
  font-size: 1.1em;
}

.notes-content :deep(h3) {
  font-size: 1em;
}

.notes-content :deep(ul),
.notes-content :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.notes-content :deep(li) {
  margin: 0.25rem 0;
  line-height: 1.5;
}

.notes-content :deep(a) {
  color: var(--accent-color);
  text-decoration: underline;
}

.notes-content :deep(strong) {
  font-weight: 700;
}

.notes-content :deep(em) {
  font-style: italic;
}

.notes-content :deep(u) {
  text-decoration: underline;
}

.notes-content :deep(s) {
  text-decoration: line-through;
}

.notes-content :deep(blockquote) {
  margin: 0.5rem 0;
  padding-left: 1rem;
  border-left: 3px solid var(--border-color);
  color: var(--text-secondary);
}

.notes-content :deep(code) {
  background: rgba(0, 0, 0, 0.05);
  padding: 0.125rem 0.25rem;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

.notes-content :deep(pre) {
  background: rgba(0, 0, 0, 0.05);
  padding: 0.5rem;
  border-radius: 4px;
  overflow-x: auto;
  margin: 0.5rem 0;
}

.notes-content :deep(pre code) {
  background: none;
  padding: 0;
}

.notes-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 0.5rem 0;
}

/* Quill editor specific classes */
.notes-content :deep(.ql-align-center) {
  text-align: center;
}

.notes-content :deep(.ql-align-right) {
  text-align: right;
}

.notes-content :deep(.ql-align-justify) {
  text-align: justify;
}

/* Dark override for code/pre only: their base is a black tint that vanishes
   on a dark background. */
:root.dark-theme .notes-content :deep(code),
:root.dark-theme .notes-content :deep(pre) {
  background: rgba(255, 255, 255, 0.05);
}

/* Higher specificity than the code rule above — keeps the base
   `pre code { background: none }` reset working in dark theme too. */
:root.dark-theme .notes-content :deep(pre code) {
  background: none;
}

/* List mode specific styles */
.link-card.is-list {
  display: flex;
  flex-direction: row;
}

.link-card.is-list .item-image {
  width: 200px;
  height: 165px;
  flex-shrink: 0;
}

.link-card.is-list .item-content {
  flex: 1;
  overflow: hidden;
}

/* Analyzing Animation Styles */
.analyzing-visual {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background: var(--surface-bg, #f8f9fa);
  display: flex;
  align-items: center;
  justify-content: center;
}

:root.dark-theme .analyzing-visual {
  background: var(--surface-card, #21262d);
}

.analyzing-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 200%;
  height: 200%;
  background-image: 
    linear-gradient(color-mix(in srgb, var(--accent-color, #1976d2) 10%, transparent) 1px, transparent 1px),
    linear-gradient(90deg, color-mix(in srgb, var(--accent-color, #1976d2) 10%, transparent) 1px, transparent 1px);
  background-size: 40px 40px;
  transform: perspective(300px) rotateX(45deg) translateY(-25%) translateZ(-50px);
  animation: gridMove 15s linear infinite;
  opacity: 0.8;
}

.analyzing-halo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  padding-bottom: 120%; /* Aspect ratio 1:1 */
  background: radial-gradient(circle, color-mix(in srgb, var(--accent-color, #1976d2) 15%, transparent) 0%, transparent 70%);
  animation: pulse 3s ease-in-out infinite;
  pointer-events: none;
}

.analyzing-scanner {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    color-mix(in srgb, var(--accent-color, #1976d2) 10%, transparent) 40%, 
    color-mix(in srgb, var(--accent-color, #1976d2) 50%, transparent) 50%, 
    color-mix(in srgb, var(--accent-color, #1976d2) 10%, transparent) 60%, 
    transparent 100%
  );
  transform: translateY(-100%);
  animation: scan 2.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  z-index: 1;
}

.analyzing-icon {
  position: relative;
  z-index: 2;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--surface-card, #ffffff);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  animation: float 3s ease-in-out infinite;
  border: 1px solid color-mix(in srgb, var(--accent-color, #1976d2) 20%, transparent);
}

:root.dark-theme .analyzing-icon {
  background: #2d333b;
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
}

.analyzing-icon i {
  font-size: 1.5rem;
  color: var(--accent-color, #1976d2);
  animation: spin-sparkle 3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes scan {
  0% { transform: translateY(-120%); }
  100% { transform: translateY(120%); }
}

@keyframes gridMove {
  0% { transform: perspective(300px) rotateX(45deg) translateY(0) translateZ(-50px); }
  100% { transform: perspective(300px) rotateX(45deg) translateY(-40px) translateZ(-50px); }
  /* Assumes 40px grid size */
}

@media (max-width: 768px) {
  .analyzing-grid {
    animation: gridMoveMobile 15s linear infinite;
    transform: scale(1.1); /* Slight Zoom instead of 3D */
    width: 100%;
    height: 100%;
  }

  @keyframes gridMoveMobile {
    0% { background-position: 0 0; }
    100% { background-position: 0 40px; }
  }
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

@keyframes spin-sparkle {
  0% { transform: rotate(0deg) scale(0.8); opacity: 0.8; }
  50% { transform: rotate(180deg) scale(1.2); opacity: 1; }
  100% { transform: rotate(360deg) scale(0.8); opacity: 0.8; }
}

/* Mobile Swipe Actions */
.card-swipe-actions {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  display: flex;
  border-radius: 16px;
  overflow: hidden;
}

.swipe-action {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
  transition: opacity 0.2s ease;
}

.swipe-action .action-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0;
  max-width: 100%;
  line-height: 1.15;
  transition: transform 0.15s ease;
  transform-origin: center;
}

.swipe-action .action-content span {
  display: block;
  text-align: center;
  max-width: 100%;
  font-size: 0.85rem;
  text-wrap: balance;
}

/* Past the trigger threshold: releasing now fires. Scale the cue so the user
   can tell a "revealed" pull from an "armed" one (the reveal starts at 1px). */
.card-swipe-actions.armed .swipe-action .action-content {
  transform: scale(1.12);
}

.card-swipe-actions.armed .swipe-action {
  filter: brightness(1.1);
}

.swipe-action.left,
.swipe-action.right {
  align-items: center;
}

/* Each panel is exactly as wide as the maximum pull, so centring the cue in
   the panel centres it in the REVEALED area at full pull, with equal breathing
   room on both sides (edge-anchoring crowded the card edge). */
.swipe-action.left,
.swipe-action.right {
  flex: 0 0 48%;
  padding: 0 1.1rem;
  box-sizing: border-box;
}

.swipe-action.right {
  margin-left: auto;
}

/* Background per resolved action (linkQuickActions.js QUICK_ACTION_META.tone) */
.swipe-action.tone-success { background-color: var(--green-500, #22c55e); }
.swipe-action.tone-danger { background-color: var(--red-500, #ef4444); }
.swipe-action.tone-info { background-color: #3b82f6; }
.swipe-action.tone-accent { background-color: var(--accent-color, #7c3aed); }
.swipe-action.tone-warning { background-color: #f59e0b; }
.swipe-action.tone-neutral { background-color: #6b7280; }

/* Long-press is an action on touch devices: suppress the OS text-selection /
   link-preview callout that would otherwise fire alongside the sheet. */
@media (pointer: coarse) {
  .link-card.quick-actions-enabled,
  .link-card.quick-actions-enabled * {
    -webkit-tap-highlight-color: transparent;
  }

  .link-card.quick-actions-enabled {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
}

.swipe-action.right.full-mode {
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
  flex: none;
  z-index: 0;
}

.card-inner-content {
  position: relative;
  z-index: 1;
  height: 100%;
  width: 100%;
  background: var(--bg-secondary);
  border-radius: 16px;
}

@media (max-width: 768px) {
  .link-card {
    background: transparent !important;
  }
}

/* Animation Overlays */
.archive-animation-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--green-500, #22c55e); /* Green background */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  animation: fadeInOut 0.3s ease forwards;
  border-radius: 16px;
}

.archive-icon-wrapper {
  color: white;
  font-size: 3rem;
  animation: scaleIcon 0.3s ease forwards;
}

.archive-icon-wrapper i {
  font-size: 3rem;
}

@keyframes fadeInOut {
  0% { opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
}

@keyframes scaleIcon {
  0% { transform: scale(0.5); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

/* Delete Shatter Animation */
.delete-animation-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100;
  /* overflow: hidden; -- Removed to allow pieces to fly out */
  border-radius: 16px;
}

.shatter-piece {
  position: absolute;
  background: var(--bg-secondary);
  width: 50%;
  height: 33.33%;
  box-shadow: inset 0 0 10px rgba(0,0,0,0.1); /* Give some texture */
}

/* Piece positions */
.shatter-piece.p1 { top: 0; left: 0; clip-path: polygon(0 0, 100% 0, 80% 100%, 0 80%); animation: flyP1 0.45s ease forwards; background-color: var(--bg-secondary); }
.shatter-piece.p2 { top: 0; right: 0; clip-path: polygon(0 0, 100% 0, 100% 80%, 20% 100%); animation: flyP2 0.45s ease forwards; background-color: var(--bg-secondary); }
.shatter-piece.p3 { top: 33%; left: 0; clip-path: polygon(0 20%, 80% 0, 100% 100%, 0 80%); animation: flyP3 0.45s ease forwards; background-color: var(--bg-secondary); }
.shatter-piece.p4 { top: 33%; right: 0; clip-path: polygon(20% 0, 100% 20%, 100% 80%, 0 100%); animation: flyP4 0.45s ease forwards; background-color: var(--bg-secondary); }
.shatter-piece.p5 { bottom: 0; left: 0; clip-path: polygon(0 20%, 100% 0, 80% 100%, 0 100%); animation: flyP5 0.45s ease forwards; background-color: var(--bg-secondary); }
.shatter-piece.p6 { bottom: 0; right: 0; clip-path: polygon(0 0, 100% 20%, 100% 100%, 20% 100%); animation: flyP6 0.45s ease forwards; background-color: var(--bg-secondary); }

/* Fly animations */
@keyframes flyP1 { to { transform: translate(-50px, -50px) rotate(-20deg); opacity: 0; } }
@keyframes flyP2 { to { transform: translate(50px, -50px) rotate(20deg); opacity: 0; } }
@keyframes flyP3 { to { transform: translate(-60px, 0) rotate(-10deg); opacity: 0; } }
@keyframes flyP4 { to { transform: translate(60px, 0) rotate(10deg); opacity: 0; } }
@keyframes flyP5 { to { transform: translate(-50px, 50px) rotate(-20deg); opacity: 0; } }
@keyframes flyP6 { to { transform: translate(50px, 50px) rotate(20deg); opacity: 0; } }

.reminder-indicator-image {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: var(--surface-card, #ffffff);
  color: var(--primary-color, #1976d2);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 10;
  border: 1px solid rgba(0,0,0,0.05);
}

:root.dark-theme .reminder-indicator-image {
  background-color: #2d333b;
  color: var(--primary-color-text, #64b5f6);
  border-color: rgba(255,255,255,0.1);
}

.price-alert-indicator-image {
  position: absolute;
  top: 8px;
  background-color: var(--surface-card, #ffffff);
  color: #16a34a;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 10;
  border: 1px solid rgba(0,0,0,0.05);
}

:root.dark-theme .price-alert-indicator-image {
  background-color: #2d333b;
  color: #4ade80;
  border-color: rgba(255,255,255,0.1);
}

/* Image-only layout's waiting-to-sync badge; `left` comes from pendingSyncBadgeLeft. */
.pending-sync-indicator-image {
  position: absolute;
  top: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px dashed var(--text-secondary);
  box-shadow: 0 2px 4px var(--shadow-light);
  z-index: 10;
}

.highlight-count-indicator-image {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: var(--surface-card, #ffffff);
  color: #b45309;
  height: 24px;
  min-width: 24px;
  padding: 0 6px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 10;
  border: 1px solid rgba(0,0,0,0.05);
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
}
.highlight-count-indicator-image .highlight-count-num {
  font-variant-numeric: tabular-nums;
}
:root.dark-theme .highlight-count-indicator-image {
  background-color: #2d333b;
  color: #fbbf24;
  border-color: rgba(255,255,255,0.1);
}

.reminder-icon-title {
  margin-left: 0.5rem;
  display: inline-flex;
  align-items: center;
  color: var(--primary-color);
  vertical-align: middle;
}

/* ========== Selection Mode Styles ========== */

.link-card.selection-mode {
  cursor: pointer;
  user-select: none;
}

.link-card.selection-mode .card-inner-content {
  transition: outline 0.15s ease, box-shadow 0.15s ease;
}

.link-card.is-selected .card-inner-content {
  outline: 2px solid var(--accent-color, #1976d2);
  outline-offset: -2px;
  box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.12);
}

.selection-checkbox-overlay {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 20;
  cursor: pointer;
}

.selection-checkbox {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid rgba(120, 120, 130, 0.5);
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.selection-checkbox:hover {
  border-color: var(--accent-color, #1976d2);
  transform: scale(1.08);
}

.selection-checkbox.checked {
  background: var(--accent-color, #1976d2);
  border-color: var(--accent-color, #1976d2);
}

.selection-checkbox.checked i {
  color: #fff;
  font-size: 14px;
  font-weight: bold;
}

:root.dark-theme .selection-checkbox {
  background: rgba(30, 30, 40, 0.92);
  border-color: rgba(180, 180, 190, 0.4);
}

:root.dark-theme .selection-checkbox.checked {
  background: var(--accent-color, #58a6ff);
  border-color: var(--accent-color, #58a6ff);
}

:root.dark-theme .link-card.is-selected .card-inner-content {
  box-shadow: 0 0 0 4px rgba(88, 166, 255, 0.2);
}

/* In selection mode, suppress normal link card hover effects and click-through */
.link-card.selection-mode .item-image {
  pointer-events: none;
}

.link-card.selection-mode .action-buttons-row,
.link-card.selection-mode .card-swipe-actions {
  display: none;
}

</style>
